/* COB LED V5 — U5 UNIVERSAL FULL SETUP EXPERIENCE BINDING / SINGLE MODAL P1/P2/P3/P4 OWNER
   FULL ASSET REPLACEMENT.
   BASE: sealed U1 clicked-offer exact setup experience.
   Scoring/recommender are read-only and are not recalculated here.
   R4S-admitted clicked offers use their own canonical lane + head family.
   Resolution order: STRICT EXACT first; STANDARD PURPOSE ARCHETYPE second.
   The schematic has one inline surface on every viewport; the obsolete
   second/full-width schematic state is intentionally removed.
   Variant selection keeps the user in the detail modal and brings the
   selected setup schematic into the modal viewport instead of jumping to top. */
(() => {
  'use strict';
  const app = window.GDX_COB_LED_V5;
  if (!app || !app.runtime || app.runtime.has('setupExperienceStrict')) return;

  app.runtime.register('setupExperienceStrict', ({ services, events, diagnostics }) => {
    let unlistenDetail = null;
    let unlistenRecommendation = null;
    let currentOfferKey = null;
    let currentContext = null;
    let activeVariant = 'MAIN';
    let side = 'LEFT';
    let initialized = false;
    let renderedCount = 0;
    let standardRenderCount = 0;
    let blockedCount = 0;
    let compatibilityRenderCount = 0;
    let detailTabsNode = null;
    let detailBodyNode = null;
    let detailModalNode = null;
    let tabHandler = null;
    let tabObserver = null;
    let modalObserver = null;
    let shellObserver = null;
    let themeObserver = null;
    let fallbackSnapshot = null;
    let lastScrollResetReason = null;
    let lastHoldReason = null;
    let rootNode = null;
    let shellNode = null;
    let workspaceNode = null;
    let globalToTopNode = null;
    let i127ScrollHandler = null;
    let i127ScrollRaf = 0;
    let i127CompactState = false;
    let i128ScrollSpyHandler = null;
    let i128ScrollSpyRaf = 0;
    let i128ScrollSpyRoot = null;
    let i128ScrollSpyButtons = [];
    let i128ActivePanelCode = 'P1';

    const value = (record) => record && record.value !== undefined ? record.value : null;
    const fold = (input) => String(input ?? '')
      .normalize('NFKC')
      .toLocaleUpperCase('tr-TR')
      .replace(/İ/g, 'I').replace(/Ş/g, 'S').replace(/Ğ/g, 'G')
      .replace(/Ü/g, 'U').replace(/Ö/g, 'O').replace(/Ç/g, 'C')
      .replace(/\s+/g, ' ').trim();
    const el = (tag, className = '', text = null) => {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (text !== null) node.textContent = String(text);
      return node;
    };

    const fullscreenIconSvg = (open = false) => open
      ? '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 3H3v6M15 3h6v6M21 15v6h-6M9 21H3v-6"/><path d="M8 8 3 3M16 8l5-5M16 16l5 5M8 16l-5 5"/></svg>'
      : '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 3H3v5M16 3h5v5M21 16v5h-5M8 21H3v-5"/><path d="M9 9 3 3M15 9l6-6M15 15l6 6M9 15l-6 6"/></svg>';
    /* FAZ08-I12.10.4 · Collapse/minimise icon: four diagonal arrows point INWARD. */
    const shrinkIconSvg = () => '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">'
      + '<path d="M3.5 3.5 9 9"/><path d="M9 9H5.5"/><path d="M9 9V5.5"/>'
      + '<path d="M20.5 3.5 15 9"/><path d="M15 9h3.5"/><path d="M15 9V5.5"/>'
      + '<path d="M3.5 20.5 9 15"/><path d="M9 15H5.5"/><path d="M9 15v3.5"/>'
      + '<path d="M20.5 20.5 15 15"/><path d="M15 15h3.5"/><path d="M15 15v3.5"/>'
      + '</svg>';

    function applyFullscreenStickerVisual(button, expanded) {
      if (!button) return;
      const open = Boolean(expanded);
      button.classList.toggle('gdx-cob-v5-setup-x__i129-zoom-sticker--open', open);
      button.classList.toggle('gdx-cob-v5-setup-x__i129-zoom-sticker--closed', !open);
      button.innerHTML = open ? shrinkIconSvg() : fullscreenIconSvg(false);
      button.dataset.gdxCobV5I12104Icon = open ? 'COLLAPSE_INWARD_ARROWS' : 'EXPAND_OUTWARD_ARROWS';
      button.dataset.state = open ? 'open' : 'closed';
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      const iconLabel = open ? 'Tam ekranı kapat' : 'Tam ekranı aç';
      button.setAttribute('title', iconLabel);
    }


    /* FAZ09.10.2 · EXTERNAL STRICT-SCHEMATIC FULLSCREEN BRIDGE
       Additive public bridge for comparison setup previews.
       Reuses the same fullscreen icon owner and the SAME live SVG DOM.
       Existing Kurulumu Gör behavior is not changed. */
    let externalFullscreenHandle = null;

    function wireExternalExpandableView({
      card,
      shell,
      sticker,
      labelTr = 'Üstten Görünüm',
      kind = 'top',
      onChange = null
    } = {}) {
      if (!card || !shell || !sticker) return null;

      const previousTabIndex = shell.getAttribute('tabindex');
      const previousRole = shell.getAttribute('role');
      const previousAriaLabel = shell.getAttribute('aria-label');
      let restoreFocus = null;
      let destroyed = false;
      let expanded = false;
      let bodyOverflowBefore = null;
      let htmlOverflowBefore = null;

      shell.dataset.gdxCobV5ExternalFullscreenToggle = '1';
      shell.dataset.gdxCobV5ExternalSchematicKind = kind;
      shell.setAttribute('role', 'button');
      shell.setAttribute('tabindex', '0');
      shell.setAttribute('aria-label', labelTr + ' şemasını tam ekran aç veya kapat');
      shell.setAttribute('aria-expanded', 'false');

      sticker.type = 'button';
      sticker.dataset.gdxCobV5ExternalFullscreenSticker = '1';
      sticker.setAttribute('aria-label', labelTr + ' şemasını tam ekran aç');
      applyFullscreenStickerVisual(sticker, false);

      const lockDocumentScroll = () => {
        if (bodyOverflowBefore !== null) return;
        bodyOverflowBefore = document.body?.style?.overflow ?? '';
        htmlOverflowBefore = document.documentElement?.style?.overflow ?? '';
        if (document.body) document.body.style.overflow = 'hidden';
        if (document.documentElement) document.documentElement.style.overflow = 'hidden';
      };

      const unlockDocumentScroll = () => {
        if (bodyOverflowBefore === null) return;
        if (document.body) document.body.style.overflow = bodyOverflowBefore;
        if (document.documentElement) document.documentElement.style.overflow = htmlOverflowBefore ?? '';
        bodyOverflowBefore = null;
        htmlOverflowBefore = null;
      };

      const onKeydown = (event) => {
        if (!expanded || event.key !== 'Escape') return;
        event.preventDefault();
        setExpanded(false, 'escape');
      };

      const setExpanded = (open, reason = 'tap') => {
        if (destroyed) return false;
        const next = Boolean(open);
        if (next === expanded) return expanded;

        if (next && externalFullscreenHandle && externalFullscreenHandle !== handle) {
          externalFullscreenHandle.setExpanded(false, 'external-owner-switch');
        }

        if (next) {
          restoreFocus = document.activeElement instanceof HTMLElement ? document.activeElement : sticker;
          lockDocumentScroll();
          card.classList.add('gdx-cob-v5-setup-x__external-fullscreen');
          shell.classList.add('gdx-cob-v5-setup-x__external-fullscreen-shell');
          card.dataset.gdxCobV5ExternalFullscreen = '1';
          shell.dataset.gdxCobV5ExternalFullscreen = '1';
          if (sticker.parentElement !== card) card.appendChild(sticker);
          sticker.dataset.gdxCobV5ExternalFullscreenPlacement = 'overlay-root';
          document.addEventListener('keydown', onKeydown, true);
          externalFullscreenHandle = handle;
        } else {
          card.classList.remove('gdx-cob-v5-setup-x__external-fullscreen');
          shell.classList.remove('gdx-cob-v5-setup-x__external-fullscreen-shell');
          card.dataset.gdxCobV5ExternalFullscreen = '0';
          shell.dataset.gdxCobV5ExternalFullscreen = '0';
          if (sticker.parentElement !== shell) shell.appendChild(sticker);
          sticker.dataset.gdxCobV5ExternalFullscreenPlacement = 'shell-overlay';
          document.removeEventListener('keydown', onKeydown, true);
          unlockDocumentScroll();
          if (externalFullscreenHandle === handle) externalFullscreenHandle = null;
        }

        expanded = next;
        shell.setAttribute('aria-expanded', next ? 'true' : 'false');
        applyFullscreenStickerVisual(sticker, next);
        sticker.setAttribute(
          'aria-label',
          labelTr + (next ? ' şemasını tam ekrandan kapat' : ' şemasını tam ekran aç')
        );

        if (next) {
          requestAnimationFrame(() => {
            shell.scrollLeft = Math.max(0, (shell.scrollWidth - shell.clientWidth) / 2);
            shell.scrollTop = Math.max(0, (shell.scrollHeight - shell.clientHeight) / 2);
          });
        } else if (restoreFocus?.isConnected) {
          try {
            restoreFocus.focus({ preventScroll: true });
            shell.dataset.gdxCobV5ExternalFocusRestored = document.activeElement === restoreFocus ? '1' : '0';
          } catch (_) {
            shell.dataset.gdxCobV5ExternalFocusRestored = '0';
          }
        }

        shell.dataset.gdxCobV5ExternalFullscreenReason = String(reason || 'tap');
        if (typeof onChange === 'function') {
          try { onChange(next, reason); } catch (_) {}
        }
        return expanded;
      };

      const toggle = (reason = 'tap') => setExpanded(!expanded, reason);

      const onShellClick = (event) => {
        if (event.target.closest('button,a,input,select,textarea')) return;
        toggle(kind + '-schematic-tap');
      };
      const onShellKeydown = (event) => {
        if (!['Enter', ' '].includes(event.key)) return;
        event.preventDefault();
        toggle(kind + '-schematic-keyboard');
      };
      const onStickerClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        toggle(kind + '-sticker-tap');
      };

      shell.addEventListener('click', onShellClick);
      shell.addEventListener('keydown', onShellKeydown);
      sticker.addEventListener('click', onStickerClick);

      const handle = Object.freeze({
        owner: 'setupExperienceStrict',
        kind,
        desktopSupported: true,
        mobileSupported: true,
        sameSvgDomRequired: true,
        setExpanded,
        toggle,
        getState: () => Object.freeze({
          expanded,
          cardConnected: Boolean(card.isConnected),
          shellConnected: Boolean(shell.isConnected),
          stickerConnected: Boolean(sticker.isConnected),
          ariaExpanded: shell.getAttribute('aria-expanded'),
          focusRestored: shell.dataset.gdxCobV5ExternalFocusRestored === '1'
        }),
        destroy() {
          if (destroyed) return;
          setExpanded(false, 'destroy');
          destroyed = true;
          shell.removeEventListener('click', onShellClick);
          shell.removeEventListener('keydown', onShellKeydown);
          sticker.removeEventListener('click', onStickerClick);
          document.removeEventListener('keydown', onKeydown, true);
          unlockDocumentScroll();
          card.classList.remove('gdx-cob-v5-setup-x__external-fullscreen');
          shell.classList.remove('gdx-cob-v5-setup-x__external-fullscreen-shell');
          delete card.dataset.gdxCobV5ExternalFullscreen;
          delete shell.dataset.gdxCobV5ExternalFullscreen;
          delete shell.dataset.gdxCobV5ExternalFullscreenToggle;
          delete shell.dataset.gdxCobV5ExternalSchematicKind;
          delete shell.dataset.gdxCobV5ExternalFullscreenReason;
          delete shell.dataset.gdxCobV5ExternalFocusRestored;
          delete sticker.dataset.gdxCobV5ExternalFullscreenSticker;
          delete sticker.dataset.gdxCobV5ExternalFullscreenPlacement;
          if (previousTabIndex === null) shell.removeAttribute('tabindex');
          else shell.setAttribute('tabindex', previousTabIndex);
          if (previousRole === null) shell.removeAttribute('role');
          else shell.setAttribute('role', previousRole);
          if (previousAriaLabel === null) shell.removeAttribute('aria-label');
          else shell.setAttribute('aria-label', previousAriaLabel);
          shell.removeAttribute('aria-expanded');
          if (externalFullscreenHandle === handle) externalFullscreenHandle = null;
        }
      });

      return handle;
    }

    function setupSurfaceText(input) {
      const source = String(input ?? '');
      let output = source
        .replace(/setup'ların/giu, 'kurulumların')
        .replace(/setup'larda/giu, 'kurulumlarda')
        .replace(/setup'ları/giu, 'kurulumları')
        .replace(/setup'tan/giu, 'kurulumdan')
        .replace(/setup'ta/giu, 'kurulumda')
        .replace(/setup'a/giu, 'kuruluma')
        .replace(/setup'ın/giu, 'kurulumun')
        .replace(/setup'ı/giu, 'kurulumu')
        .replace(/setup/giu, 'kurulum');
      if (/^\s*Setup/u.test(source)) output = output.replace(/^(\s*)kurulum/u, '$1Kurulum');
      return output;
    }

    function variantSetupLabel(record) {
      if (record.variantKey === 'MAIN') return 'ANA KURULUM';
      if (record.variantKey === 'ALT_A') return 'ALTERNATİF KURULUM 1';
      if (record.variantKey === 'ALT_B') return 'ALTERNATİF KURULUM 2';
      return setupSurfaceText(record.variantLabelTr || 'KURULUM').toLocaleUpperCase('tr-TR');
    }

    function inlineSchematicOptions() {
      const mobile = typeof window.matchMedia === 'function' &&
        window.matchMedia('(max-width:760px)').matches;
      return Object.freeze({
        mobile,
        width: mobile ? 560 : 920,
        height: mobile ? 620 : 700
      });
    }

    function ensureDetailNodes() {
      detailModalNode = detailModalNode || document.getElementById(services.config.ids.detailModal);
      detailTabsNode = detailTabsNode || document.getElementById(services.config.ids.detailTabs);
      detailBodyNode = detailBodyNode || detailModalNode?.querySelector('.gdx-cob-v5__modal-body') || null;
    }

    /* FAZ08-I12.6 · Compact Dual Sticky primary navigation.
       Product gallery remains available inside the content surfaces, so the standalone
       Gallery primary tab is suppressed from interaction without touching product media data. */
    function syncI126PrimaryTabs() {
      ensureDetailNodes();
      const galleryTab = detailTabsNode?.querySelector('[data-tab="gallery"]') || null;
      if (!galleryTab) return false;
      galleryTab.hidden = true;
      galleryTab.disabled = true;
      galleryTab.tabIndex = -1;
      galleryTab.setAttribute('aria-hidden', 'true');
      galleryTab.dataset.gdxCobV5I126Suppressed = '1';
      return true;
    }

    /* FAZ08-I12.7 · Auto-compacting sticky owner.
       IMPORTANT: only navigation chrome is compacted. Setup cards/SVG/text are never
       scaled, cropped, translated, or height-reduced by this phase. */
    function setI127CompactState(next, reason = 'scroll') {
      ensureDetailNodes();
      const value = Boolean(next && modalVisible() && setupTabSelected());
      if (i127CompactState === value && detailModalNode?.classList.contains('gdx-cob-v5__modal--i127-auto-compact') === value) return value;
      i127CompactState = value;
      detailModalNode?.classList.toggle('gdx-cob-v5__modal--i127-auto-compact', value);
      if (detailModalNode) {
        detailModalNode.dataset.gdxCobV5I127Compact = value ? '1' : '0';
        detailModalNode.dataset.gdxCobV5I127CompactReason = String(reason || 'scroll');
      }
      return value;
    }

    function syncI127AutoCompact(reason = 'scroll') {
      ensureDetailNodes();
      if (!detailBodyNode || !modalVisible() || !setupTabSelected()) return setI127CompactState(false, reason);
      const top = Number(detailBodyNode.scrollTop || 0);
      /* Hysteresis prevents visual chatter around the activation edge. */
      const enterAt = 112;
      const leaveAt = 28;
      const next = i127CompactState ? top > leaveAt : top >= enterAt;
      return setI127CompactState(next, reason);
    }

    function installI127AutoCompact() {
      ensureDetailNodes();
      if (!detailBodyNode || i127ScrollHandler) return Boolean(i127ScrollHandler);
      i127ScrollHandler = () => {
        if (i127ScrollRaf) return;
        i127ScrollRaf = requestAnimationFrame(() => {
          i127ScrollRaf = 0;
          syncI127AutoCompact('detail-body-scroll');
        });
      };
      detailBodyNode.addEventListener('scroll', i127ScrollHandler, { passive: true });
      syncI127AutoCompact('install');
      return true;
    }

    function uninstallI127AutoCompact() {
      if (detailBodyNode && i127ScrollHandler) detailBodyNode.removeEventListener('scroll', i127ScrollHandler);
      i127ScrollHandler = null;
      if (i127ScrollRaf) cancelAnimationFrame(i127ScrollRaf);
      i127ScrollRaf = 0;
      setI127CompactState(false, 'destroy');
    }

    /* FAZ08-I12.8 · Scroll-spy active-section owner.
       The real detail modal scroll surface determines P1/P2/P3/P4 aria-current.
       This restores the active Moss Bronze state while scrolling, not only after clicks. */
    function setI128ActivePanel(code, reason = 'scroll-spy') {
      const normalized = ['P1','P2','P3','P4'].includes(String(code || '').toUpperCase())
        ? String(code).toUpperCase() : 'P1';
      i128ActivePanelCode = normalized;
      i128ScrollSpyButtons.forEach((btn) => {
        const active = btn.dataset.r74Jump === normalized;
        btn.setAttribute('aria-current', active ? 'true' : 'false');
        btn.dataset.gdxCobV5I128ScrollSpyActive = active ? '1' : '0';
      });
      if (i128ScrollSpyRoot) {
        i128ScrollSpyRoot.dataset.gdxCobV5I128ActivePanel = normalized;
        i128ScrollSpyRoot.dataset.gdxCobV5I128ActiveReason = String(reason || 'scroll-spy');
      }
      return normalized;
    }

    function syncI128ScrollSpy(reason = 'scroll-spy') {
      ensureDetailNodes();
      const root = i128ScrollSpyRoot;
      if (!detailBodyNode || !root || !modalVisible() || !setupTabSelected()) return null;
      const panels = [...root.querySelectorAll(':scope > [data-r74-panel]')];
      if (!panels.length) return null;
      const bodyRect = detailBodyNode.getBoundingClientRect();
      const modal = detailModalNode;
      const tabs = modal?.querySelector('#gdxCobV5DetailTabs');
      const nav = root.querySelector(':scope > .gdx-cob-v5-setup-x__r74-nav');
      const stickyReserve = (tabs?.getBoundingClientRect().height || 0) + (nav?.getBoundingClientRect().height || 0);
      const activationY = bodyRect.top + Math.min(Math.max(stickyReserve + 18, 72), bodyRect.height * 0.42);
      let active = panels[0];
      panels.forEach((panel) => {
        const rect = panel.getBoundingClientRect();
        if (rect.top <= activationY) active = panel;
      });
      if (active.getBoundingClientRect().bottom < activationY) {
        const intersecting = panels.find((panel) => {
          const rect = panel.getBoundingClientRect();
          return rect.top <= activationY && rect.bottom >= activationY;
        });
        if (intersecting) active = intersecting;
      }
      return setI128ActivePanel(active.dataset.r74Panel || 'P1', reason);
    }

    function installI128ScrollSpy(root, buttons) {
      ensureDetailNodes();
      if (detailBodyNode && i128ScrollSpyHandler) detailBodyNode.removeEventListener('scroll', i128ScrollSpyHandler);
      if (i128ScrollSpyRaf) cancelAnimationFrame(i128ScrollSpyRaf);
      i128ScrollSpyRaf = 0;
      i128ScrollSpyRoot = root || null;
      i128ScrollSpyButtons = Array.isArray(buttons) ? buttons : [];
      if (!detailBodyNode || !i128ScrollSpyRoot || !i128ScrollSpyButtons.length) return false;
      i128ScrollSpyHandler = () => {
        if (i128ScrollSpyRaf) return;
        i128ScrollSpyRaf = requestAnimationFrame(() => {
          i128ScrollSpyRaf = 0;
          syncI128ScrollSpy('detail-body-scroll');
        });
      };
      detailBodyNode.addEventListener('scroll', i128ScrollSpyHandler, { passive:true });
      requestAnimationFrame(() => syncI128ScrollSpy('install'));
      return true;
    }

    function uninstallI128ScrollSpy() {
      if (detailBodyNode && i128ScrollSpyHandler) detailBodyNode.removeEventListener('scroll', i128ScrollSpyHandler);
      i128ScrollSpyHandler = null;
      if (i128ScrollSpyRaf) cancelAnimationFrame(i128ScrollSpyRaf);
      i128ScrollSpyRaf = 0;
      i128ScrollSpyRoot = null;
      i128ScrollSpyButtons = [];
      i128ActivePanelCode = 'P1';
    }

    function ensureGlobalNodes() {
      rootNode = rootNode || document.getElementById(services.config.ids.root || 'gdxCobV5Root');
      shellNode = shellNode || document.getElementById(services.config.ids.shell || 'gdxCobV5Shell');
      workspaceNode = workspaceNode || shellNode?.querySelector('.gdx-cob-v5__workspace') || null;
      if (!globalToTopNode && rootNode) {
        globalToTopNode = el('button', 'gdx-cob-v5__global-to-top', 'BAŞA DÖN');
        globalToTopNode.id = 'gdxCobV5GlobalToTop';
        globalToTopNode.type = 'button';
        globalToTopNode.hidden = true;
        globalToTopNode.dataset.surface = 'panel';
        globalToTopNode.setAttribute('aria-label', 'COB LED panelinin başına dön');
        globalToTopNode.addEventListener('click', resetActiveSurfaceScroll);
        rootNode.append(globalToTopNode);
      }
    }

    function modalVisible() {
      ensureDetailNodes();
      return Boolean(
        detailModalNode &&
        !detailModalNode.hidden &&
        detailModalNode.getAttribute('aria-hidden') !== 'true'
      );
    }

    function shellVisible() {
      ensureGlobalNodes();
      return Boolean(shellNode && !shellNode.hidden && shellNode.getAttribute('aria-hidden') !== 'true');
    }

    function syncGlobalToTop() {
      ensureGlobalNodes();
      if (!globalToTopNode) return;
      const inDetail = modalVisible();
      globalToTopNode.hidden = !(inDetail || shellVisible());
      globalToTopNode.dataset.surface = inDetail ? 'detail' : 'panel';
      globalToTopNode.setAttribute(
        'aria-label',
        inDetail ? 'Ürün detayının başına dön' : 'COB LED panelinin başına dön'
      );
    }

    function resetDetailScroll(reason = 'manual') {
      ensureDetailNodes();
      if (!detailBodyNode) return false;
      if (typeof detailBodyNode.scrollTo === 'function') {
        detailBodyNode.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      } else {
        detailBodyNode.scrollTop = 0;
        detailBodyNode.scrollLeft = 0;
      }
      lastScrollResetReason = reason;
      setI127CompactState(false, reason);
      return true;
    }

    function focusSetupSchematic(reason = 'variant-schematic-focus') {
      ensureDetailNodes();
      const mount = document.getElementById(services.config.ids.detailSetup);
      const schematic =
        mount?.querySelector('[data-gdx-cob-v5-i104-master="1"]') ||
        mount?.querySelector('.gdx-cob-v5-setup-x__i104-view-card--top') ||
        mount?.querySelector('.gdx-cob-v5-setup-x__schematic-card');
      if (!detailBodyNode || !schematic) return false;

      const scrollerRect = detailBodyNode.getBoundingClientRect();
      const targetRect = schematic.getBoundingClientRect();
      const viewportHeight = detailBodyNode.clientHeight;
      const inset = 16;
      let targetTop = detailBodyNode.scrollTop + targetRect.top - scrollerRect.top;

      if (targetRect.height < viewportHeight - (inset * 2)) {
        targetTop -= (viewportHeight - targetRect.height) / 2;
      } else {
        targetTop -= inset;
      }

      const maxTop = Math.max(0, detailBodyNode.scrollHeight - viewportHeight);
      const top = Math.min(maxTop, Math.max(0, targetTop));
      const reduceMotion = typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (typeof detailBodyNode.scrollTo === 'function') {
        detailBodyNode.scrollTo({ top, left: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
      } else {
        detailBodyNode.scrollTop = top;
        detailBodyNode.scrollLeft = 0;
      }
      lastScrollResetReason = reason;
      return true;
    }

    function resetPanelScroll(reason = 'global-to-top-panel') {
      ensureGlobalNodes();
      if (!workspaceNode) return false;
      if (typeof workspaceNode.scrollTo === 'function') {
        workspaceNode.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      } else {
        workspaceNode.scrollTop = 0;
        workspaceNode.scrollLeft = 0;
      }
      lastScrollResetReason = reason;
      return true;
    }

    function resetActiveSurfaceScroll() {
      const reset = modalVisible()
        ? resetDetailScroll('global-to-top-detail')
        : resetPanelScroll('global-to-top-panel');
      if (reset) {
        requestAnimationFrame(() => {
          const focusTarget = modalVisible()
            ? detailModalNode?.querySelector('.gdx-cob-v5__modal-header h2')
            : shellNode?.querySelector('.gdx-cob-v5__title');
          focusTarget?.focus?.({ preventScroll: true });
        });
      }
      return reset;
    }

    function setupTabSelected() {
      ensureDetailNodes();
      return detailTabsNode
        ?.querySelector('[data-tab="setup"]')
        ?.getAttribute('aria-selected') === 'true';
    }

    function syncModalMode(forcedTab = null) {
      ensureDetailNodes();
      if (!detailModalNode) return;
      const hasDynamicRoot = Boolean(
        document.getElementById(services.config.ids.detailSetup)
          ?.querySelector('.gdx-cob-v5-setup-x')
      );
      const setupActive = forcedTab ? forcedTab === 'setup' : setupTabSelected();
      const visible = !detailModalNode.hidden && detailModalNode.getAttribute('aria-hidden') !== 'true';
      detailModalNode.classList.toggle(
        'gdx-cob-v5__modal--setup-wide',
        Boolean(hasDynamicRoot && setupActive && visible)
      );
    }

    function snapshotFallback(mount) {
      fallbackSnapshot = mount ? [...mount.childNodes].map((node) => node.cloneNode(true)) : null;
    }

    function restoreFallback(mount) {
      if (!mount || !fallbackSnapshot) return false;
      mount.replaceChildren(...fallbackSnapshot.map((node) => node.cloneNode(true)));
      syncModalMode();
      return true;
    }

    /* COB-V5-USE-U5-UNIVERSAL-SETUP-BINDING-EXACT-FIRST-STANDARD-SECOND-V1
       Customer-facing resolver contract:
       1) clicked offer must be R4S-admitted for the selected purpose;
       2) clicked offer uses its own canonical BARE/SET lane + head family;
       3) STRICT EXACT geometry is always first and authoritative;
       4) only when strict exact is unavailable, setupStandardLibrary may provide
          a verified STANDARD geometry for the SAME purpose;
       5) scoring/ranking/F-P/Setup Readiness/package truth remain read-only. */
    function resolveContextForPurpose(offerKey, purposeKey, currentResult = null) {
      const purpose = String(purposeKey || '');
      if (!purpose || !offerKey) {
        return { ok: false, reason: 'MISSING_RECOMMENDATION_CONTEXT' };
      }

      const detail = services.productExperience.getDetail(offerKey, purpose);
      const offer = services.catalog.getOffer(offerKey);
      if (!detail || !offer) return { ok: false, reason: 'OFFER_DETAIL_NOT_FOUND' };
      if (detail.r4sRankAdmissible !== true) {
        return { ok: false, reason: 'OFFER_NOT_R4S_ADMITTED' };
      }

      const headFamilyKey = String(detail.headFamilyKey || '');
      const detailLane = String(detail.lane || value(offer.purchaseLane) || '');
      if (!headFamilyKey) return { ok: false, reason: 'HEAD_FAMILY_NOT_FOUND' };
      if (!['BARE_LIGHT', 'SINGLE_READY_SET'].includes(detailLane)) {
        return { ok: false, reason: 'DETAIL_LANE_NOT_SUPPORTED' };
      }

      const resultMatchesPurpose =
        currentResult && String(currentResult?.purposeKey || '') === purpose;
      const currentTop3Item = resultMatchesPurpose
        ? ((currentResult?.top3 || []).find((item) =>
            String(item.offerKey || '') === String(offerKey)
          ) || null)
        : null;

      if (
        currentTop3Item &&
        String(currentTop3Item.headFamilyKey || '') &&
        String(currentTop3Item.headFamilyKey || '') !== headFamilyKey
      ) {
        return { ok: false, reason: 'RECOMMENDATION_FAMILY_MISMATCH' };
      }

      const recommendationItem = currentTop3Item || Object.freeze({
        offerKey: String(offerKey),
        title: detail.title || String(value(offer.title) || offerKey),
        headFamilyKey,
        images: Array.isArray(detail.images) ? detail.images : Object.freeze([])
      });

      const laneLabelTr = detail.laneLabelTr ||
        (detailLane === 'SINGLE_READY_SET' ? 'Tek Işıklı Hazır Set' : 'Yalnız Ana Işık Ünitesi');
      const result = Object.freeze({
        purposeKey: purpose,
        purposeLabel:
          (resultMatchesPurpose ? currentResult?.purposeLabel : null) ||
          detail.whyPresentation?.purposeLabelTr ||
          purpose,
        lane: detailLane,
        laneLabelTr
      });

      const bindingContext = {
        purposeKey: purpose,
        offerKey: String(offerKey),
        purchaseLane: detailLane,
        detailLane,
        headFamilyKey,
        recommendationItem
      };

      const exactBinding =
        services.setupGeometryStrict.getResolvedContextStatus(bindingContext);

      let binding = exactBinding;
      let bindingTier = 'EXACT';
      let bindingOwner = 'setupGeometryStrict';

      if (!exactBinding?.ok || exactBinding.records?.length !== 3) {
        const standardOwner = services.setupStandardLibrary;
        const standardBinding =
          standardOwner?.getStandardContextStatus?.(bindingContext) || null;

        if (!standardBinding?.ok || standardBinding.records?.length !== 3) {
          return {
            ok: false,
            reason:
              standardBinding?.reason ||
              exactBinding?.reason ||
              'UNIVERSAL_SETUP_BINDING_HOLD'
          };
        }

        binding = standardBinding;
        bindingTier = String(standardBinding.tier || 'STANDARD');
        bindingOwner = 'setupStandardLibrary';
      }

      const records = binding.records;
      const allowed = bindingTier === 'EXACT'
        ? records.every((record) =>
            services.setupGeometryStrict.isResolvedOfferAllowed(
              record,
              offerKey,
              detailLane
            ))
        : records.every((record) =>
            services.setupStandardLibrary?.isStandardOfferAllowed?.(
              record,
              offerKey,
              detailLane
            ) === true
          );

      if (!allowed) {
        return {
          ok: false,
          reason: bindingTier === 'EXACT'
            ? 'RESOLVED_OFFER_LANE_NOT_ALLOWED'
            : 'STANDARD_OFFER_LANE_NOT_ALLOWED'
        };
      }

      let scene = null;
      if (bindingTier === 'EXACT') {
        const main = records.find((record) => record.variantKey === 'MAIN');
        if (main?.legacySceneNodeKey) {
          const candidate = services.setupSceneMapStrict.get(purpose, headFamilyKey);
          if (candidate && services.setupSceneMapStrict.isOfferAllowed(candidate, offerKey)) {
            scene = candidate;
          }
        }
      }

      return {
        ok: true,
        result,
        purposeKey: purpose,
        lane: detailLane,
        offerKey: String(offerKey),
        detail,
        offer,
        headFamilyKey,
        recommendationItem,
        records,
        bindingSource: binding.source,
        bindingReason: binding.reason,
        bindingTier,
        bindingOwner,
        standardReferencePurposeKey: binding.referencePurposeKey || null,
        standardReferenceHeadFamilyKey: binding.referenceHeadFamilyKey || null,
        standardReferenceOfferKey: binding.referenceOfferKey || null,
        scene
      };
    }

    function contextFor(offerKey) {
      const currentResult = services.recommender.getLast?.();
      const purposeKey = String(currentResult?.purposeKey || '');
      return resolveContextForPurpose(offerKey, purposeKey, currentResult);
    }
    function parseRequiredModifier(record) {
      const text = fold(`${record?.modifierTr || ''} ${record?.modifierExactSize || ''}`);
      const rectangular = text.match(/(\d{2,3})\s*[X×]\s*(\d{2,3})\s*CM/);
      const diameter = text.match(/(?:Ø\s*)?(\d{2,3})\s*CM\s*(?:CAP|DIAMETER|OCTAGON|PARABOLIK|LANTERN)?/);
      let type = 'GENERIC_MODIFIER';
      if (/GOBO|PROJEKSIYON|PROJECTION/.test(text)) type = 'SPECIAL_OPTIC';
      else if (/OCTAGON|PARABOLIK/.test(text)) type = 'OCTAGON_OR_PARABOLIC';
      else if (/SOFTBOX|DIFUZYON|DIFUZOR|LANTERN/.test(text)) type = 'SOFTBOX_OR_DIFFUSION';
      else if (/REFLEKTOR/.test(text)) type = 'REFLECTOR';
      return {
        type,
        need: fold(record?.modifierNeed || 'PREFERRED'),
        widthCm: rectangular ? Number(rectangular[1]) : null,
        heightCm: rectangular ? Number(rectangular[2]) : null,
        diameterCm: !rectangular && diameter ? Number(diameter[1]) : null,
        labelTr: record?.modifierTr || 'Işık şekillendirici',
        sizeTr: record?.modifierExactSize || ''
      };
    }

    function evaluateCompatibility(offer, record) {
      const pkg = offer?.package || {};
      const lane = value(offer?.purchaseLane);
      const required = parseRequiredModifier(record);
      const included = {
        type: value(pkg.modifierType),
        widthCm: Number(value(pkg.modifierWidthCm)) || null,
        heightCm: Number(value(pkg.modifierHeightCm)) || null,
        diameterCm: Number(value(pkg.modifierDiameterCm)) || null,
        softbox: value(pkg.softboxIncluded) === true,
        umbrella: value(pkg.umbrellaIncluded) === true,
        reflector: value(pkg.reflectorIncluded) === true
      };
      const includedAny = included.softbox || included.umbrella || included.reflector;
      const includedSize = included.widthCm && included.heightCm
        ? `${included.widthCm}×${included.heightCm} cm`
        : included.diameterCm ? `${included.diameterCm} cm` : '';
      const includedLabel = included.softbox
        ? `${includedSize ? `${includedSize} ` : ''}softbox`
        : included.umbrella ? 'şemsiye'
          : included.reflector ? 'reflektör'
            : 'ışık şekillendirici';
      let status = 'UNKNOWN';

      if (required.need === 'OPTIONAL') {
        status = includedAny ? 'OPTIONAL_INCLUDED' : 'OPTIONAL_NOT_INCLUDED';
      } else if (required.type === 'SPECIAL_OPTIC') {
        status = 'ACCESSORY_REQUIRED';
      } else if (required.type === 'REFLECTOR') {
        status = included.reflector ? 'INCLUDED_EXACT' : 'ACCESSORY_REQUIRED';
      } else if (required.type === 'OCTAGON_OR_PARABOLIC') {
        if (included.type === 'OCTAGON_OR_PARABOLIC') {
          status = required.diameterCm && included.diameterCm
            ? included.diameterCm >= required.diameterCm ? 'INCLUDED_EXACT' : 'INCLUDED_SMALLER_THAN_RECOMMENDED'
            : 'INCLUDED_COMPATIBLE';
        } else if (included.umbrella) status = 'INCLUDED_COMPATIBLE';
        else status = lane === 'BARE_LIGHT' ? 'ACCESSORY_REQUIRED' : 'UNKNOWN';
      } else if (required.type === 'SOFTBOX_OR_DIFFUSION') {
        if (included.softbox) {
          if (required.widthCm && required.heightCm && included.widthCm && included.heightCm) {
            const exact =
              (required.widthCm === included.widthCm && required.heightCm === included.heightCm) ||
              (required.widthCm === included.heightCm && required.heightCm === included.widthCm);
            if (exact) status = 'INCLUDED_EXACT';
            else {
              const requiredArea = required.widthCm * required.heightCm;
              const includedArea = included.widthCm * included.heightCm;
              status = includedArea < requiredArea
                ? 'INCLUDED_SMALLER_THAN_RECOMMENDED'
                : 'INCLUDED_LARGER_OR_DIFFERENT';
            }
          } else status = 'INCLUDED_COMPATIBLE';
        } else if (included.umbrella) status = 'INCLUDED_COMPATIBLE';
        else status = lane === 'BARE_LIGHT' ? 'ACCESSORY_REQUIRED' : 'UNKNOWN';
      } else {
        status = includedAny ? 'INCLUDED_COMPATIBLE'
          : lane === 'BARE_LIGHT' ? 'ACCESSORY_REQUIRED' : 'UNKNOWN';
      }

      const requiredText = `${required.labelTr}${required.sizeTr ? ` · ${required.sizeTr}` : ''}`;
      const messages = {
        OPTIONAL_INCLUDED: included.reflector
          ? 'Pakette reflektör bulunuyor; bu kurulumda kullanımı isteğe bağlıdır. Daha dar bir renk lekesi için snoot veya projeksiyon aparatı ayrıca gerekebilir.'
          : `Paketteki ${includedLabel} isteğe bağlı olarak kullanılabilir; bu dahil aksesuar için ek satın alma gerekmez.`,
        OPTIONAL_NOT_INCLUDED: `${requiredText} zorunlu değildir ve bu teklifte doğrulanmış olarak görünmüyor. Daha kontrollü dağılım istenirse ayrıca temin edilebilir.`,
        INCLUDED_EXACT: `Paketteki ${includedSize || includedLabel} bu kurulum önerisiyle birebir uyumludur.`,
        INCLUDED_COMPATIBLE: `Paketteki ${includedLabel} bu kurulumda kullanılabilir; yerinde ince ayar yapın.`,
        INCLUDED_SMALLER_THAN_RECOMMENDED: `Öneri ${required.sizeTr || required.labelTr}; pakette ${includedSize || 'daha küçük bir şekillendirici'} bulunur. Kapsama daralabilir ve gölge geçişi sertleşebilir.`,
        INCLUDED_LARGER_OR_DIFFERENT: `Paketteki ${includedSize || includedLabel}, önerilen ${required.sizeTr || required.labelTr} ile aynı değildir; kadraj ve gücü yerinde dengeleyin.`,
        ACCESSORY_REQUIRED: `${requiredText} bu teklife dahil değildir; bu kurulum için ayrıca temin edilmelidir.`,
        UNKNOWN: `Paket içeriği ile “${requiredText}” önerisinin birebir uyumu doğrulanamadı.`
      };
      return Object.freeze({
        status,
        required,
        included,
        messageTr: messages[status],
        blocksClaimOfReadySetup: ['ACCESSORY_REQUIRED', 'UNKNOWN'].includes(status)
      });
    }

    const COMPATIBILITY_TITLES = Object.freeze({
      OPTIONAL_INCLUDED: 'Pakette var · kullanımı isteğe bağlı',
      OPTIONAL_NOT_INCLUDED: 'İsteğe bağlı aksesuar',
      INCLUDED_EXACT: 'Birebir uyumlu ve dahil',
      INCLUDED_COMPATIBLE: 'Pakette var · kullanılabilir',
      INCLUDED_SMALLER_THAN_RECOMMENDED: 'Pakette var · önerilenden küçük',
      INCLUDED_LARGER_OR_DIFFERENT: 'Pakette var · ölçüsü farklı',
      ACCESSORY_REQUIRED: 'Ek aksesuar gerekli',
      UNKNOWN: 'Paket uyumu doğrulanamadı'
    });

    const STAND_COMPATIBILITY_TITLES = Object.freeze({
      PASS: 'Işık ayağı yüksekliği uygun',
      INSUFFICIENT: 'Işık ayağı önerilen yüksekliğe yetmiyor',
      PRESENT_HEIGHT_UNKNOWN: 'Işık ayağı var · yüksekliği doğrulanamadı',
      MISSING: 'Işık ayağı pakette yok',
      NOT_APPLICABLE: 'Işık ayağı paket karşılaştırması uygulanmaz'
    });

    function normalizeBandLabel(record) {
      const raw = String(record?.lightBand || record?.distanceBandLabelTr || '').trim();
      const f = fold(raw);
      if (/YAKIN/.test(f)) return 'Yakın';
      if (/ORTA/.test(f)) return 'Orta';
      if (/GENIS/.test(f) && /UZAK/.test(f)) return 'Geniş / Uzak';
      if (/UZAK/.test(f)) return 'Uzak';
      return raw || 'Yerinde belirlenir';
    }

    function evaluateStandCompatibility(offer, record) {
      const pkg = offer?.package || {};
      const lane = value(offer?.purchaseLane);
      const requiredHeightM = Number(record?.lightHeightM);
      const requiredHeightCm = Number.isFinite(requiredHeightM) ? Math.round(requiredHeightM * 100) : null;
      const included = value(pkg.standIncluded) === true;
      const includedHeightCm = Number(value(pkg.standHeightCm)) || null;

      let status = 'NOT_APPLICABLE';
      if (lane === 'BARE_LIGHT') status = 'MISSING';
      else if (!included) status = 'MISSING';
      else if (!includedHeightCm || !requiredHeightCm) status = 'PRESENT_HEIGHT_UNKNOWN';
      else status = includedHeightCm >= requiredHeightCm ? 'PASS' : 'INSUFFICIENT';

      const requiredText = Number.isFinite(requiredHeightM)
        ? services.setupRendererStrict.fmtM(requiredHeightM)
        : 'önerilen yüksekliği';
      let messageTr = '';
      if (status === 'PASS') {
        messageTr = `Setteki ${includedHeightCm} cm ışık ayağı, ${requiredText} önerilen ışık yüksekliğini karşılar.`;
      } else if (status === 'INSUFFICIENT') {
        messageTr = `Setteki ${includedHeightCm} cm ışık ayağı, ${requiredText} önerilen ışık yüksekliğini karşılamaz.`;
      } else if (status === 'PRESENT_HEIGHT_UNKNOWN') {
        messageTr = `Sette ışık ayağı bulunuyor; ${requiredText} için yeterli yüksekliğe ulaşıp ulaşmadığı doğrulanamadı.`;
      } else if (lane === 'BARE_LIGHT') {
        messageTr = `Işık ayağı bu BARE LIGHT teklife dahil değildir; ${requiredText} sağlayabilen uygun bir ayak gerekir.`;
      } else {
        messageTr = `Bu teklifte ışık ayağı doğrulanmadı; ${requiredText} sağlayabilen uygun bir ayak gerekir.`;
      }

      return Object.freeze({
        status,
        included,
        includedHeightCm,
        requiredHeightCm,
        requiredHeightM: Number.isFinite(requiredHeightM) ? requiredHeightM : null,
        titleTr: STAND_COMPATIBILITY_TITLES[status],
        messageTr
      });
    }

    function conciseModifierTruth(result) {
      if (!result) return '';
      const req = result.required || {};
      const inc = result.included || {};
      const requiredText = `${req.sizeTr || req.labelTr || 'önerilen modifier'}`.trim();
      const includedSize = inc.widthCm && inc.heightCm
        ? `${inc.widthCm}×${inc.heightCm} cm`
        : inc.diameterCm ? `${inc.diameterCm} cm` : '';
      if (result.status === 'INCLUDED_EXACT') return `Paketteki ${includedSize || 'modifier'} önerilen ${requiredText} ile uyumludur.`;
      if (result.status === 'INCLUDED_SMALLER_THAN_RECOMMENDED') return `Paketteki ${includedSize || 'modifier'}, önerilen ${requiredText} ölçüsünden küçüktür.`;
      if (result.status === 'INCLUDED_LARGER_OR_DIFFERENT') return `Paketteki ${includedSize || 'modifier'}, önerilen ${requiredText} ölçüsüyle aynı değildir.`;
      if (result.status === 'INCLUDED_COMPATIBLE' || result.status === 'OPTIONAL_INCLUDED') return 'Paketteki ışık şekillendirici bu kurulumda kullanılabilir.';
      if (result.status === 'ACCESSORY_REQUIRED') return `${requiredText} bu teklife dahil değildir; ayrıca temin edilmelidir.`;
      if (result.status === 'OPTIONAL_NOT_INCLUDED') return `${requiredText} isteğe bağlıdır ve pakette doğrulanmamıştır.`;
      return 'Önerilen modifier ile paket içeriğinin birebir uyumu doğrulanamadı.';
    }

    function variantFocusTr(record) {
      const band = normalizeBandLabel(record);
      const bandKey = fold(band);
      if (bandKey.includes('YAKIN')) return 'Yakın mesafede ışık yumuşaklığı, yön ve modifier konumu';
      if (bandKey.includes('ORTA')) return 'Orta mesafede kapsama, yön ve modifier boyutu dengesi';
      return 'Geniş kapsama, büyük modifier ve yüksek ışık ayağı gereksinimi';
    }

    function roleTargetModel() {
      try {
        return services.roleTargetPresentation?.buildModel?.() || null;
      } catch (_) {
        return null;
      }
    }

    function variantRolePhraseTr(record) {
      const roleKey = String(record?.recommendedRole || record?.roleKey || '');
      if (/^(RGB_|EFEKT_)/.test(roleKey)) {
        const role = setupSurfaceText(services.setupRendererStrict.roleLabel?.(record) || '');
        return role ? role.toLocaleLowerCase('tr-TR') : 'rgb ışık';
      }
      return 'ana ışık';
    }

    function variantCharacterTr(record) {
      const band = normalizeBandLabel(record);
      const bandKey = fold(band);
      if (record?.variantKey === 'MAIN') {
        if (bandKey.includes('YAKIN')) return `öncelikli, yakın ve kontrollü ${variantRolePhraseTr(record)} kurulumu`;
        if (bandKey.includes('ORTA')) return `öncelikli, dengeli orta mesafe ${variantRolePhraseTr(record)} kurulumu`;
        return `öncelikli, geniş kapsamalı ${variantRolePhraseTr(record)} kurulumu`;
      }
      if (bandKey.includes('YAKIN')) return 'daha yakın ve kontrollü alternatif kurulum';
      if (bandKey.includes('ORTA')) return 'kapsama ile kontrol arasında denge sağlayan orta mesafeli alternatif kurulum';
      return 'daha geniş kapsama ve uzak çalışma sağlayan alternatif kurulum';
    }

    function projectVariantNarrative(context, record) {
      const modifier = evaluateCompatibility(context.offer, record);
      const stand = evaluateStandCompatibility(context.offer, record);
      const band = normalizeBandLabel(record);
      const fmtM = services.setupRendererStrict.fmtM;
      const fmtDeg = services.setupRendererStrict.fmtDeg;
      const purpose = setupSurfaceText(context.result?.purposeLabel || record?.purposeLabel || 'Seçili çekim amacı');
      const target = setupSurfaceText(record?.targetTr || 'hedef');
      const direction = record?.mirrorAllowed !== false
        ? 'kamera ekseninin sağ veya sol önünden'
        : setupSurfaceText(record?.lightSideTr || 'önerilen yönden');
      const modifierText = `${setupSurfaceText(record?.modifierExactSize || record?.lightShaperExactSizeTr || '')} ${setupSurfaceText(record?.modifierTr || record?.lightShaperTr || 'ışık şekillendirici')}`.replace(/\s+/g, ' ').trim();
      const intro = setupSurfaceText(record?.noraPurposeIntroTr || '');
      const main = `${purpose} amacı için ${variantCharacterTr(record)}. Işığı ${direction} yaklaşık ${fmtDeg(record.horizontalAngleDeg)} açıyla, ${target.toLocaleLowerCase('tr-TR')} için ${fmtM(record.lightToTargetM)} mesafede ve ${fmtM(record.lightHeightM)} yükseklikte konumlandırın; çalışma bandı ${band}. ${modifierText} önerilir.`;
      const packageParts = [conciseModifierTruth(modifier), stand.messageTr].filter(Boolean);
      const caution = `${variantFocusTr(record)}.${packageParts.length ? ` ${packageParts.join(' ')}` : ''}`;
      const passive = (record?.passiveFillType || record?.passiveFillTr)
        ? `Şemadaki pasif dolgu için ${setupSurfaceText(record.passiveFillType || record.passiveFillTr).toLocaleLowerCase('tr-TR')} kullanılabilir; pakete dahil olup olmadığı paket içeriğinden ayrıca doğrulanır.`
        : '';
      const disclosureBase = setupSurfaceText(record?.noraExternalDisclosureTr || '');
      const disclosure = [disclosureBase, passive].filter(Boolean).join(' ');

      return Object.freeze({
        version: 'COB-FAZ07-I8.8-VARIANT-NARRATIVE-PROJECTOR-V1',
        variantKey: record?.variantKey || '',
        distanceBandTr: band,
        introTr: intro,
        mainTr: main,
        cautionTr: caution,
        disclosureTr: disclosure,
        modifierCompatibility: modifier,
        standCompatibility: stand,
        passiveFillExplanationTr: passive
      });
    }

    function metric(label, text) {
      const card = el('div', 'gdx-cob-v5-setup-x__metric');
      card.append(el('strong', '', label), el('span', '', text));
      return card;
    }

    function dataCard(title, className = '') {
      const card = el('section', `gdx-cob-v5-setup-x__data-card ${className}`.trim());
      card.append(el('h4', '', title));
      return card;
    }


    function inlineMetricStrip(record) {
      const strip = el('div', 'gdx-cob-v5-setup-x__inline-metric-strip');
      const fmtM = services.setupRendererStrict.fmtM;
      const fmtDeg = services.setupRendererStrict.fmtDeg;
      const targetModel = roleTargetModel();
      const noun = setupSurfaceText(targetModel?.copyTargetNounTr || '').trim();
      const kelvinRange = Number.isFinite(Number(record.kelvinMinK)) && Number.isFinite(Number(record.kelvinMaxK))
        ? `${Number(record.kelvinMinK).toLocaleString('tr-TR')}–${Number(record.kelvinMaxK).toLocaleString('tr-TR')} K`
        : 'Yerinde belirlenir';
      [
        [noun ? `Işık–${noun}` : 'Işık–hedef', fmtM(record.lightToTargetM)],
        ['Yatay açı', fmtDeg(record.horizontalAngleDeg)],
        [noun ? `Kamera–${noun}` : 'Kamera–hedef', fmtM(record.cameraToTargetM)],
        ['Kelvin', kelvinRange]
      ].forEach(([label, text]) => {
        const item = el('div', 'gdx-cob-v5-setup-x__inline-metric');
        item.append(el('strong', '', text), el('span', '', label));
        strip.append(item);
      });
      return strip;
    }

    function compatibilityCard(context, record) {
      const result = evaluateCompatibility(context.offer, record);
      compatibilityRenderCount += 1;
      const card = dataCard(
        'Işık Şekillendirici ve Paket / Aksesuar Durumu',
        `gdx-cob-v5-setup-x__package gdx-cob-v5-setup-x__package--${result.status.toLowerCase()}`
      );
      card.dataset.compatibilityStatus = result.status;
      const recommended = el('p', 'gdx-cob-v5-setup-x__package-recommended');
      recommended.append(
        el('strong', '', 'Kurulum önerisi'),
        document.createTextNode(` ${result.required.labelTr}${result.required.sizeTr ? ` · ${result.required.sizeTr}` : ''}`)
      );
      const status = el('div', 'gdx-cob-v5-setup-x__package-status');
      status.append(
        el('strong', '', COMPATIBILITY_TITLES[result.status] || COMPATIBILITY_TITLES.UNKNOWN),
        el('p', '', setupSurfaceText(result.messageTr))
      );
      const standResult = evaluateStandCompatibility(context.offer, record);
      const standStatus = el('div', 'gdx-cob-v5-setup-x__package-status gdx-cob-v5-setup-x__package-status--stand');
      standStatus.dataset.gdxCobV5I88StandStatus = standResult.status;
      standStatus.append(
        el('strong', '', standResult.titleTr),
        el('p', '', setupSurfaceText(standResult.messageTr))
      );
      card.append(recommended, status, standStatus);

      const items = context.detail.packageItems || [];
      const actual = el('div', 'gdx-cob-v5-setup-x__actual-package');
      actual.append(el('strong', '', 'Bu teklifte doğrulanan içerik'));
      if (items.length) {
        const list = el('ul');
        items.forEach((item) => list.append(el('li', '', item)));
        actual.append(list);
      } else {
        actual.append(el('p', '', context.lane === 'BARE_LIGHT'
          ? 'Bu teklif yalnız ışık ünitesi kulvarındadır; aksesuarlar ayrıca doğrulanmalıdır.'
          : 'Bu teklifte açıkça doğrulanmış paket aksesuarı bulunmuyor.'));
      }
      card.append(actual);
      return card;
    }

    function noraCard(context, record) {
      const honest = (Array.isArray(record.warningsTr) && record.warningsTr.length > 0)
        || (context.lane === 'SINGLE_READY_SET' && !(context.detail.packageItems || []).length);
      const decisionPolicy = services.noraDecisionPolicy || null;
      const decision = !honest && decisionPolicy && typeof decisionPolicy.resolve === 'function'
        ? decisionPolicy.resolve(context.offerKey || context.detail?.offerKey || context.offer?.offerKey, context.purposeKey || '')
        : null;
      const roleName = honest
        ? 'honestCaution'
        : (decision?.noraRole === 'expertComment' || decision?.noraRole === 'honestCaution'
          ? decision.noraRole
          : 'setupPointing');
      const role = services.config.nora?.roles?.[roleName];
      const projected = projectVariantNarrative(context, record);
      const card = dataCard('NORA Kurulum Önerisi', 'gdx-cob-v5-setup-x__nora');
      card.dataset.gdxCobV5I88VariantNarrative = '1';
      card.dataset.variantKey = projected.variantKey;
      card.dataset.distanceBand = projected.distanceBandTr;
      card.dataset.modifierCompatibility = projected.modifierCompatibility?.status || '';
      card.dataset.standCompatibility = projected.standCompatibility?.status || '';
      if (role) {
        card.dataset.noraRole = role.key;
        card.dataset.gdxCobV5NoraDecisionRole = roleName;
        card.style.setProperty('--gdx-nora-desktop-max', `${Math.min(Number(role.desktopMaxWidthPx) || 160, 160)}px`);
        card.style.setProperty('--gdx-nora-mobile-max', `${Math.min(Number(role.mobileMaxWidthPx) || 78, 78)}px`);
        const visual = el('div', 'gdx-cob-v5-setup-x__nora-visual');
        visual.style.width = 'clamp(78px, 10vw, 160px)';
        visual.style.maxWidth = 'clamp(78px, 10vw, 160px)';
        visual.style.minWidth = '0';
        const image = document.createElement('img');
        image.src = role.src;
        image.alt = role.alt;
        image.width = role.desktopMaxWidthPx;
        image.height = role.desktopMaxWidthPx;
        image.loading = 'eager';
        image.decoding = 'async';
        visual.append(image);
        card.append(visual);
      }
      if (honest || roleName === 'honestCaution') card.querySelector('h4').textContent = 'NORA';
      if (projected.introTr) card.append(el('p', 'gdx-cob-v5-setup-x__nora-intro', projected.introTr));
      card.append(el('p', 'gdx-cob-v5-setup-x__nora-main', projected.mainTr));
      if (projected.cautionTr) {
        const caution = el('p', 'gdx-cob-v5-setup-x__nora-note');
        caution.append(el('strong', '', 'Dikkat odağı: '), document.createTextNode(projected.cautionTr));
        card.append(caution);
      }
      if (projected.disclosureTr) card.append(el('p', 'gdx-cob-v5-setup-x__nora-disclosure', projected.disclosureTr));
      return card;
    }

    function kelvinCard(record) {
      const card = dataCard('Başlangıç Kelvin Önerisi', 'gdx-cob-v5-setup-x__kelvin');
      const range = Number.isFinite(Number(record.kelvinMinK)) && Number.isFinite(Number(record.kelvinMaxK))
        ? `${Number(record.kelvinMinK).toLocaleString('tr-TR')}–${Number(record.kelvinMaxK).toLocaleString('tr-TR')} K`
        : 'Yerinde belirlenir';
      card.append(el('strong', 'gdx-cob-v5-setup-x__kelvin-range', range));
      const notes = [record.kelvinNote1Tr, record.kelvinNote2Tr].filter(Boolean);
      if (notes.length) card.append(el('p', '', setupSurfaceText(notes.join(' · '))));
      card.append(el('p', 'gdx-cob-v5-setup-x__kelvin-truth', 'Bu Kelvin aralığı, kurulum ve kamera renk sıcaklığı için başlangıç önerisidir; garanti edilmiş fotometrik çıktı değildir.'));
      return card;
    }

    function measuresCard(record) {
      const card = dataCard('Temel Ölçüler');
      const grid = el('div', 'gdx-cob-v5-setup-x__metric-grid');
      const fmtM = services.setupRendererStrict.fmtM;
      const fmtDeg = services.setupRendererStrict.fmtDeg;
      [
        ['Işık–hedef', fmtM(record.lightToTargetM)],
        ['Yatay açı', fmtDeg(record.horizontalAngleDeg)],
        ['Kamera–hedef', fmtM(record.cameraToTargetM)],
        ['Işık mesafe bandı', record.lightBand || '—'],
        ['Kamera mesafe bandı', record.cameraBand || '—'],
        ['Hedef', record.targetTr || '—']
      ].forEach(([label, text]) => grid.append(metric(label, text)));
      card.append(grid);
      return card;
    }

    function renderScene(context, record) {
      if (!record.isMainSetup || !context.scene) return null;
      const sceneUrl = services.setupSceneMapStrict.getApprovedUrl(
        context.purposeKey,
        context.headFamilyKey,
        context.offerKey
      );
      if (!sceneUrl) return null;
      const figure = el('figure', 'gdx-cob-v5-setup-x__scene');
      const image = document.createElement('img');
      image.src = sceneUrl;
      image.alt = `${context.result.purposeLabel} — ${context.detail.title} ana kurulum sahnesi`;
      image.loading = 'lazy';
      figure.append(
        image,
        el('figcaption', '', 'Gerçek kurulum görseli · seçili ürün ve ana kurulum geometrisi')
      );
      return figure;
    }

    function productImagesFor(context) {
      const currentDetail = services.productExperience.getDetail(
        context.offerKey,
        context.purposeKey
      );
      const candidates = [
        currentDetail?.images,
        context.detail?.images,
        context.recommendationItem?.images
      ];
      const resolved = candidates.find((images) =>
        Array.isArray(images) && images.some((image) => image?.src)
      ) || [];
      return resolved.filter((image) => image?.src).slice(0, 8);
    }

    function applyProductImage(node, image, fallbackTitle) {
      node.src = image.src;
      node.alt = image.alt || fallbackTitle;
      node.decoding = 'async';
      if (Number.isFinite(image.width) && image.width > 0) node.width = image.width;
      else node.removeAttribute('width');
      if (Number.isFinite(image.height) && image.height > 0) node.height = image.height;
      else node.removeAttribute('height');
    }

    function renderProductMedia(context) {
      const figure = el('figure', 'gdx-cob-v5__detail-product-media gdx-cob-v5-setup-x__product-media');
      figure.dataset.surface = 'setup-exact';
      const stage = el('div', 'gdx-cob-v5__detail-product-stage');
      const images = productImagesFor(context);
      const productImage = images[0] || null;
      let mainImage = null;
      if (productImage) {
        mainImage = document.createElement('img');
        mainImage.className = 'gdx-cob-v5__detail-product-main-image';
        applyProductImage(mainImage, productImage, context.detail.title);
        mainImage.loading = 'eager';
        mainImage.fetchPriority = 'high';
        stage.append(mainImage);
      } else {
        stage.append(el('p', 'gdx-cob-v5__detail-product-missing', 'Ürün ana görseli mağaza verisinde bulunamadı.'));
      }
      figure.append(stage);
      if (images.length > 1) {
        const previews = el('div', 'gdx-cob-v5__detail-product-previews');
        previews.setAttribute('aria-label', 'Ürün görselleri');
        const previewButtons = [];
        images.forEach((previewImage, index) => {
          const preview = el('button', 'gdx-cob-v5__detail-product-preview');
          preview.type = 'button';
          preview.setAttribute('aria-label', `${index + 1}. ürün görselini büyüt`);
          preview.setAttribute('aria-current', index === 0 ? 'true' : 'false');
          const thumb = document.createElement('img');
          const imageNo = index + 1;
          applyProductImage(thumb, previewImage, `${context.detail.title} — ${imageNo}. görsel`);
          thumb.loading = imageNo <= 4 ? 'eager' : 'lazy';
          preview.append(thumb, el('span', 'gdx-cob-v5__detail-product-preview-number', String(imageNo)));
          preview.addEventListener('click', () => {
            if (!mainImage) return;
            applyProductImage(mainImage, previewImage, `${context.detail.title} — ${imageNo}. görsel`);
            mainImage.loading = 'eager';
            figure.dataset.activeImage = String(imageNo);
            previewButtons.forEach((button) => {
              button.setAttribute('aria-current', button === preview ? 'true' : 'false');
            });
          });
          previewButtons.push(preview);
          previews.append(preview);
        });
        figure.dataset.activeImage = '1';
        figure.append(previews);
      }
      return figure;
    }


    /* DYN324-R7.4 — single-modal P1/P2/P3/P4 information architecture. */
    const r74Money = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' });

    function r74PriceText(priceMinor) {
      const valid = priceMinor !== null && priceMinor !== undefined && priceMinor !== '' && Number.isFinite(Number(priceMinor));
      return valid ? r74Money.format(Number(priceMinor) / 100) : 'Canlı fiyat yok';
    }

    function r74PhaseHeader(code, title, text) {
      const header = el('header', 'gdx-cob-v5-setup-x__r74-phase-head');
      header.append(
        el('span', 'gdx-cob-v5-setup-x__r74-phase-code', ({P1:'ÖZET',P2:'PROFİL',P3:'KURULUM',P4:'ALTERNATİF'}[code] || code)),
        el('div', 'gdx-cob-v5-setup-x__r74-phase-copy')
      );
      const copy = header.lastElementChild;
      copy.append(el('h4', '', title));
      if (text) copy.append(el('p', '', text));
      return header;
    }

    function r74Section(code, title, text, modifier = '') {
      const section = el('section', `gdx-cob-v5-setup-x__r74-panel gdx-cob-v5-setup-x__r74-panel--${code.toLowerCase()} ${modifier}`.trim());
      section.dataset.r74Panel = code;
      section.id = `gdxCobV5R74${code}`;
      section.append(r74PhaseHeader(code, title, text));
      return section;
    }

    function r74ScoreCard(row) {
      const valueNumber = row?.value !== null && row?.value !== undefined && Number.isFinite(Number(row.value))
        ? Math.round(Number(row.value))
        : null;
      const card = el('article', 'gdx-cob-v5-setup-x__r74-score');
      card.dataset.gdxCobV5R74Score = row?.key || '';
      card.dataset.scoreValue = valueNumber === null ? '' : String(valueNumber);
      const gauge = el('div', 'gdx-cob-v5-setup-x__r74-score-gauge');
      gauge.style.setProperty('--gdx-r74-score', String(valueNumber ?? 0));
      gauge.dataset.empty = valueNumber === null ? 'true' : 'false';
      gauge.append(
        el('strong', '', valueNumber === null ? '—' : String(valueNumber)),
        el('small', '', '/100')
      );
      const copy = el('div', 'gdx-cob-v5-setup-x__r74-score-copy');
      copy.append(
        el('strong', '', row?.labelTr || 'Skor'),
        el('p', '', row?.explanationTr || 'Mevcut değerlendirme değeridir; burada yeniden hesaplanmaz.')
      );
      card.append(gauge, copy);
      return card;
    }

    function r74P1(context) {
      const section = r74Section(
        'P1',
        'Ürün ve karar özeti',
        'Ürün görselleri, canlı fiyat ve üç karar skoru birlikte gösterilir.'
      );
      const grid = el('div', 'gdx-cob-v5-setup-x__r74-p1-grid');
      grid.append(renderProductMedia(context));
      const summary = el('div', 'gdx-cob-v5-setup-x__r74-product-summary');
      summary.append(
        el('p', 'gdx-cob-v5-setup-x__r74-kicker', 'SEÇİLİ ÜRÜN'),
        el('h5', '', context.recommendationItem.title || context.detail.title)
      );
      const chips = el('div', 'gdx-cob-v5-setup-x__r74-chips');
      [
        context.result.purposeLabel,
        context.result.laneLabelTr || (context.lane === 'SINGLE_READY_SET' ? 'Tek Işıklı Hazır Set' : 'Yalnız Ana Işık Ünitesi'),
        context.headFamilyKey
      ].filter(Boolean).forEach((text) => chips.append(el('span', '', text)));
      summary.append(chips);

      const scores = el('div', 'gdx-cob-v5-setup-x__r74-scores');
      const scoreRows = context.detail.whyPresentation?.scoreExplanations || [
        { key: 'purpose', labelTr: 'Amaç Uyumu', value: context.detail.purposeScore, explanationTr: 'Seçili kullanım amacındaki teknik uygunluk.' },
        { key: 'fp', labelTr: 'Genel F/P', value: context.detail.generalFpScore, explanationTr: 'Fiyatına karşı sunduğu toplam değeri gösterir.' },
        { key: 'role', labelTr: 'Rol Uyumu', value: context.detail.roleCompatibilityScore, explanationTr: 'Işık görevine uygunluk; sıralamayı etkilemez.' }
      ];
      scoreRows.slice(0, 3).forEach((row) => scores.append(r74ScoreCard(row)));
      summary.append(scores);

      const price = el('div', 'gdx-cob-v5-setup-x__r74-price');
      price.dataset.gdxCobV5R74LivePrice = 'true';
      price.dataset.priceMinor = context.detail.priceMinor !== null && context.detail.priceMinor !== undefined && context.detail.priceMinor !== '' && Number.isFinite(Number(context.detail.priceMinor)) ? String(Number(context.detail.priceMinor)) : '';
      price.append(
        el('span', '', 'CANLI FİYAT'),
        el('strong', '', r74PriceText(context.detail.priceMinor)),
        el('small', '', context.detail.available ? 'Stokta · canlı mağaza verisi' : 'Stokta değil · canlı mağaza verisi')
      );
      summary.append(price);
      grid.append(summary);
      section.append(grid);
      return section;
    }

    function r74UsageProfile(context) {
      const block = el('div', 'gdx-cob-v5-setup-x__r74-profile');
      block.dataset.gdxCobV5R74Profile = 'true';
      const rows = context.detail.whyPresentation?.usageProfile || [];
      rows.slice(0, 3).forEach((row) => {
        const card = el('article', 'gdx-cob-v5-setup-x__r74-profile-card');
        card.append(el('span', '', row.labelTr || 'Profil'), el('strong', '', row.valueTr || '—'));
        block.append(card);
      });
      return block;
    }

    function r74SelectionSummary(context, record) {
      const host = el('div', 'gdx-cob-v5-setup-x__r74-selection');
      host.dataset.r74Selection = record.variantKey;
      const scene = renderScene(context, record);
      if (scene) host.append(scene);
      const card = el('article', 'gdx-cob-v5-setup-x__r74-selection-card');
      card.append(
        el('span', 'gdx-cob-v5-setup-x__r74-kicker', 'SEÇİLİ DİNAMİK KURULUM'),
        el('strong', '', variantSetupLabel(record)),
        el('p', '', record.variantKey === 'MAIN'
          ? 'Bu ürün ve amaç için öncelikli geometrik başlangıç kurulumu seçili.'
          : setupSurfaceText(record.variantReasonTr || 'Alternatif geometri seçili.'))
      );
      const facts = el('div', 'gdx-cob-v5-setup-x__r74-selection-facts');
      [
        ['Işık–hedef', services.setupRendererStrict.fmtM(record.lightToTargetM)],
        ['Yatay açı', services.setupRendererStrict.fmtDeg(record.horizontalAngleDeg)],
        ['Kamera–hedef', services.setupRendererStrict.fmtM(record.cameraToTargetM)]
      ].forEach(([label, text]) => facts.append(metric(label, text)));
      card.append(facts);
      host.append(card);
      return host;
    }

    function r74AlternativeComment(context, record) {
      const box = el('section', 'gdx-cob-v5-setup-x__r74-alt-comment');
      box.dataset.gdxCobV5R74AlternativeComment = 'true';
      const alternatives = context.records.filter((row) => row.variantKey !== record.variantKey);
      box.append(el('strong', '', 'Alternatif kullanım yorumu'));
      const selectedText = record.variantKey === 'MAIN'
        ? 'Ana kurulum, seçili amaç için öncelikli geometridir.'
        : `${variantSetupLabel(record)} seçildi: ${setupSurfaceText(record.variantReasonTr || 'farklı bir kadraj veya çalışma mesafesi için alternatif geometri.')}`;
      box.append(el('p', '', selectedText));
      if (alternatives.length) {
        box.append(el('p', '', alternatives.map((row) =>
          `${variantSetupLabel(row)}: ${setupSurfaceText(row.variantReasonTr || `${services.setupRendererStrict.fmtM(row.lightToTargetM)} ışık mesafesi ve ${services.setupRendererStrict.fmtDeg(row.horizontalAngleDeg)} açı alternatifi.`)}`
        ).join(' · ')));
      }
      return box;
    }

    function r74RiskBlock(context, selectedRecord) {
      const host = el('div', 'gdx-cob-v5-setup-x__r74-risk');
      host.dataset.gdxCobV5R74Risk = 'true';
      host.append(compatibilityCard(context, selectedRecord));
      if (Array.isArray(selectedRecord.warningsTr) && selectedRecord.warningsTr.length) {
        const warning = el('section', 'gdx-cob-v5-setup-x__warning');
        warning.append(el('strong', '', 'Dikkat / kurulum uyarısı'));
        selectedRecord.warningsTr.forEach((text) => warning.append(el('p', '', setupSurfaceText(text))));
        host.append(warning);
      }
      const truth = el('section', 'gdx-cob-v5-setup-x__truth');
      truth.append(
        el('strong', '', 'Ölçüm ve uygulama sınırı'),
        el('p', '', 'Ölçüler uygulanabilir başlangıç geometrisidir; üretici fotometrik ölçümü veya garantili lux sonucu değildir. Mekâna, hedefe ve kamera ayarına göre yerinde ince ayar gerekir.')
      );
      host.append(truth);
      return host;
    }

    function r74AlternativeEvaluation(context, selectedRecord) {
      const host = el('div', 'gdx-cob-v5-setup-x__r74-alternatives');
      host.dataset.gdxCobV5R74Alternatives = 'true';
      context.records.forEach((record) => {
        const compatibility = evaluateCompatibility(context.offer, record);
        const card = el('article', 'gdx-cob-v5-setup-x__r74-alt-card');
        card.dataset.r74Variant = record.variantKey;
        card.dataset.selected = record.variantKey === selectedRecord.variantKey ? 'true' : 'false';
        card.dataset.compatibilityStatus = compatibility.status;
        card.append(
          el('span', 'gdx-cob-v5-setup-x__r74-alt-tag', variantSetupLabel(record)),
          el('strong', '', record.variantKey === 'MAIN' ? 'Öncelikli geometri' : setupSurfaceText(record.variantReasonTr || 'Alternatif kullanım geometrisi')),
          el('p', '', `${services.setupRendererStrict.fmtM(record.lightToTargetM)} · ${services.setupRendererStrict.fmtDeg(record.horizontalAngleDeg)} · ${record.lightBand || '—'}`),
          el('small', '', COMPATIBILITY_TITLES[compatibility.status] || COMPATIBILITY_TITLES.UNKNOWN)
        );
        host.append(card);
      });
      return host;
    }

    function i104Theme() {
      const root = document.querySelector('.gdx-cob-v5');
      const mode = String(root?.dataset?.gdxCobV5Theme || 'light');
      if (mode === 'dark') return 'dark';
      if (mode === 'system' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
      return 'light';
    }

    function i104TechnicalRows(record) {
      const fmtM = services.setupRendererStrict.fmtM;
      const fmtDeg = services.setupRendererStrict.fmtDeg;
      const targetModel = roleTargetModel();
      const noun = setupSurfaceText(targetModel?.copyTargetNounTr || '').trim();
      const targetReference = setupSurfaceText(targetModel?.targetReferenceLabelTr || '').trim();
      const kelvin = Number.isFinite(Number(record.kelvinMinK)) && Number.isFinite(Number(record.kelvinMaxK))
        ? `${Number(record.kelvinMinK).toLocaleString('tr-TR')}–${Number(record.kelvinMaxK).toLocaleString('tr-TR')} K`
        : 'Yerinde belirlenir';
      return Object.freeze([
        Object.freeze({ key:'lightToTarget', label:noun ? `Işık–${noun}` : 'Işık–hedef', value:fmtM(record.lightToTargetM) }),
        Object.freeze({ key:'angle', label:'Yatay açı', value:fmtDeg(record.horizontalAngleDeg) }),
        Object.freeze({ key:'cameraToTarget', label:noun ? `Kamera–${noun}` : 'Kamera–hedef', value:fmtM(record.cameraToTargetM) }),
        Object.freeze({ key:'lightHeight', label:'Işık yüksekliği', value:fmtM(record.lightHeightM) }),
        Object.freeze({ key:'targetHeight', label:targetReference || 'Hedef referansı', value:fmtM(record.targetReferenceHeightM) }),
        Object.freeze({ key:'tilt', label:'Aşağı eğim', value:fmtDeg(record.verticalTiltDeg) }),
        Object.freeze({ key:'kelvin', label:'Kelvin', value:kelvin }),
        Object.freeze({ key:'band', label:'Çalışma bandı', value:record.lightBand || '—' })
      ]);
    }

    function i104PresentationModel(context, record) {
      const projected = projectVariantNarrative(context, record);
      const theme = i104Theme();
      const signature = services.setupRendererStrict.geometrySignature(record, { side });
      const metrics = i104TechnicalRows(record).slice(0, 4);
      return Object.freeze({
        version:'COB-FAZ07-I10.4-EXACT-SHARE-PARITY-V1',
        offerKey:String(context.offerKey || ''),
        purposeKey:String(context.purposeKey || ''),
        purposeLabel:String(context.result?.purposeLabel || ''),
        lane:String(context.lane || ''),
        title:String(context.detail?.title || ''),
        variantKey:String(record.variantKey || ''),
        variantLabel:String(variantSetupLabel(record) || ''),
        setupRecordId:String(record.setupRecordId || record.id || ''),
        side,
        theme,
        geometrySignature:signature,
        targetPresentation:roleTargetModel(),
        metrics,
        technicalRows:i104TechnicalRows(record),
        nora:Object.freeze({
          intro:String(projected.introTr || ''),
          main:String(projected.mainTr || ''),
          caution:String(projected.cautionTr || ''),
          disclosure:String(projected.disclosureTr || '')
        })
      });
    }

    function i104MasterCard(context, record) {
      const presentation = i104PresentationModel(context, record);
      const master = el('section', 'gdx-cob-v5-setup-x__i104-master');
      master.dataset.gdxCobV5I104Master = '1';
      master.dataset.geometrySignature = presentation.geometrySignature;
      master.dataset.variantKey = presentation.variantKey;
      master.dataset.theme = presentation.theme;
      master.dataset.gdxCobV5I104Presentation = JSON.stringify(presentation);

      const head = el('header', 'gdx-cob-v5-setup-x__i104-head');
      const copy = el('div');
      copy.append(
        el('span', 'gdx-cob-v5-setup-x__i104-kicker', `SEÇİLİ AMAÇ · ${presentation.purposeLabel || 'COB LED KURULUMU'}`),
        el('h4', '', 'Ölçülü Kurulum Şeması'),
        el('p', '', `${variantSetupLabel(record)} · Üstten ve yandan görünüm aynı geometri kaydından üretilir.`)
      );
      const mirror = el('button', 'gdx-cob-v5-button gdx-cob-v5-button--secondary gdx-cob-v5-setup-x__mirror', '↔ Sağ / sol aynala');
      mirror.type = 'button';
      mirror.setAttribute('aria-label', 'Sağ / sol aynala');
      mirror.dataset.gdxCobV5MirrorChip = '1';
      mirror.classList.add('gdx-cob-v5-setup-x__mirror--header-chip', 'gdx-cob-v5-setup-x__mirror--top-view-header');
      head.append(copy);

      const metrics = inlineMetricStrip(record);
      metrics.classList.add('gdx-cob-v5-setup-x__i104-metrics');

      const visual = el('div', 'gdx-cob-v5-setup-x__i104-visual');

      /* FAZ08-I12.10.5 · Mobile fullscreen setup-share dock.
         Entry point only: it reuses the existing Universal Setup Share service. */
      const fullscreenShareService = () => {
        const svc = window.GDX_COB_LED_V5_SHARE_SETUP;
        return svc && svc.ready ? svc : null;
      };
      const fullscreenSharePreparedState = () => {
        const svc = fullscreenShareService();
        if (!svc) return { svc:null, prepared:null, valid:false, lifecycle:null };
        try { if (typeof svc.refresh === 'function') svc.refresh(); } catch (_) {}
        const prepared = typeof svc.getLastPrepared === 'function' ? svc.getLastPrepared() : null;
        const lifecycle = typeof svc.getLifecycleState === 'function' ? svc.getLifecycleState() : null;
        const valid = Boolean(prepared && (!lifecycle || (
          lifecycle.prepared === true && lifecycle.currentSignature && lifecycle.preparedSignature === lifecycle.currentSignature
        )));
        return { svc, prepared:valid ? prepared : null, valid, lifecycle };
      };
      const syncFullscreenShareDock = (dock, message = '') => {
        if (!dock) return false;
        const prepareBtn = dock.querySelector('[data-i12105-share-prepare]');
        const shareBtn = dock.querySelector('[data-i12105-share-now]');
        const download = dock.querySelector('[data-i12105-share-download]');
        const status = dock.querySelector('[data-i12105-share-status]');
        const state = fullscreenSharePreparedState();
        const ready = Boolean(state.svc);
        const prepared = Boolean(state.valid && state.prepared);
        if (prepareBtn) {
          prepareBtn.disabled = !ready;
          prepareBtn.textContent = prepared ? 'Paylaşım Kartını Yenile' : 'Paylaşım Kartını Hazırla';
        }
        if (shareBtn) shareBtn.hidden = !prepared;
        if (download) {
          download.hidden = !prepared;
          if (prepared && state.prepared && state.prepared.dataUrl) {
            download.href = state.prepared.dataUrl;
            download.download = 'canfoto-cob-kurulum-paylasim-karti.png';
          } else download.removeAttribute('href');
        }
        if (status) status.textContent = message || (prepared ? 'Paylaşım kartı hazır.' : (ready ? 'Seçili kurulumu paylaşabilirsiniz.' : 'Paylaşım servisi hazırlanıyor…'));
        dock.dataset.gdxCobV5I12105Prepared = prepared ? '1' : '0';
        return prepared;
      };
      const ensureFullscreenShareDock = (card) => {
        if (!card) return null;
        let dock = card.querySelector(':scope > [data-gdx-cob-v5-i12105-share-dock="1"]');
        if (dock) { syncFullscreenShareDock(dock); return dock; }
        dock = el('section', 'gdx-cob-v5-setup-x__i12105-share-dock');
        dock.dataset.gdxCobV5I12105ShareDock = '1';
        dock.setAttribute('aria-label', 'Setup paylaşımı');
        dock.innerHTML = ''
          + '<div class="gdx-cob-v5-setup-x__i12105-share-head">'
          +   '<span class="gdx-cob-v5-setup-x__i12105-share-kicker">SETUP PAYLAŞIMI</span>'
          +   '<span class="gdx-cob-v5-setup-x__i12105-share-status" data-i12105-share-status>Seçili kurulumu paylaşabilirsiniz.</span>'
          + '</div>'
          + '<div class="gdx-cob-v5-setup-x__i12105-share-actions">'
          +   '<button type="button" class="gdx-cob-v5-setup-x__i12105-share-btn gdx-cob-v5-setup-x__i12105-share-btn--primary" data-i12105-share-prepare>Paylaşım Kartını Hazırla</button>'
          +   '<button type="button" class="gdx-cob-v5-setup-x__i12105-share-btn gdx-cob-v5-setup-x__i12105-share-btn--secondary" data-i12105-share-now hidden>Şimdi Paylaş</button>'
          +   '<a class="gdx-cob-v5-setup-x__i12105-share-btn gdx-cob-v5-setup-x__i12105-share-btn--secondary" data-i12105-share-download hidden>PNG İndir</a>'
          + '</div>';
        card.appendChild(dock);
        dock.hidden = !card.classList.contains('gdx-cob-v5-setup-x__i129-mobile-fullscreen');
        const prepareBtn = dock.querySelector('[data-i12105-share-prepare]');
        const shareBtn = dock.querySelector('[data-i12105-share-now]');
        const status = dock.querySelector('[data-i12105-share-status]');
        prepareBtn?.addEventListener('click', async (event) => {
          event.preventDefault(); event.stopPropagation();
          const svc = fullscreenShareService();
          if (!svc || typeof svc.prepare !== 'function') { syncFullscreenShareDock(dock, 'Paylaşım servisi henüz hazır değil.'); return; }
          prepareBtn.disabled = true;
          if (status) status.textContent = 'Paylaşım kartı hazırlanıyor…';
          try {
            if (typeof svc.refresh === 'function') svc.refresh();
            const result = await svc.prepare();
            syncFullscreenShareDock(dock, result && result.ok ? 'Paylaşım kartı hazır.' : 'Kart hazırlanamadı; tekrar deneyin.');
          } catch (_) { syncFullscreenShareDock(dock, 'Kart hazırlanamadı; tekrar deneyin.'); }
        });
        shareBtn?.addEventListener('click', async (event) => {
          event.preventDefault(); event.stopPropagation();
          const svc = fullscreenShareService();
          if (!svc || typeof svc.shareNow !== 'function') { syncFullscreenShareDock(dock, 'Önce paylaşım kartını hazırlayın.'); return; }
          shareBtn.disabled = true;
          if (status) status.textContent = 'Paylaşım açılıyor…';
          try {
            const result = await svc.shareNow();
            syncFullscreenShareDock(dock, result && result.ok ? 'Paylaşım işlemi tamamlandı.' : 'Paylaşım kapatıldı veya tamamlanamadı.');
          } catch (_) { syncFullscreenShareDock(dock, 'Paylaşım tamamlanamadı.'); }
          finally { shareBtn.disabled = false; }
        });
        dock.addEventListener('click', (event) => event.stopPropagation());
        syncFullscreenShareDock(dock);
        return dock;
      };

      /* FAZ08-I12.9A · Shared mobile fullscreen owner for BOTH setup views.
         Same live SVG DOM is reused; no geometry clone/recompute. */
      const wireI129ExpandableView = (card, shell, sticker, kind, labelTr) => {
        const isTop = kind === 'top';
        shell.dataset.gdxCobV5I129FullscreenToggle = '1';
        shell.dataset.gdxCobV5I129SchematicKind = kind;
        shell.setAttribute('role', 'button');
        shell.setAttribute('tabindex', '0');
        shell.setAttribute('aria-label', labelTr + ' şemasını tam ekran aç veya kapat');
        shell.setAttribute('aria-expanded', 'false');
        if (isTop) shell.dataset.gdxCobV5I128FullscreenToggle = '1'; /* backward QA compatibility */

        sticker.type = 'button';
        sticker.dataset.gdxCobV5ControlStyle = 'ALT06_CORNER_TAB';
        sticker.dataset.gdxCobV5I129ZoomSticker = kind;
        sticker.setAttribute('aria-label', labelTr + ' şemasını büyüt');
        applyFullscreenStickerVisual(sticker, false);

        const clearOtherExpandedView = () => {
          master.querySelectorAll('[data-gdx-cob-v5-i129-fullscreen-toggle="1"]').forEach((otherShell) => {
            if (otherShell === shell) return;
            const otherCard = otherShell.closest('.gdx-cob-v5-setup-x__i104-view-card');
            const otherSticker = otherCard?.querySelector('[data-gdx-cob-v5-i129-zoom-sticker]');
            otherCard?.classList.remove('gdx-cob-v5-setup-x__i129-mobile-fullscreen','gdx-cob-v5-setup-x__i128-mobile-fullscreen');
            otherShell.setAttribute('aria-expanded','false');
            otherShell.dataset.gdxCobV5I129Fullscreen = '0';
            if (otherSticker) {
              if (otherSticker.parentElement !== otherShell) otherShell.appendChild(otherSticker);
              applyFullscreenStickerVisual(otherSticker, false);
              otherSticker.dataset.gdxCobV5I129Placement = 'shell-overlay';
              otherSticker.setAttribute('aria-label', (otherShell.dataset.gdxCobV5I129SchematicKind === 'side' ? 'Yandan Görünüm' : 'Üstten Görünüm') + ' şemasını tam ekran aç');
            }
          });
        };

        const setExpanded = (open, reason = 'tap') => {
          const mobile = typeof window.matchMedia === 'function' && window.matchMedia('(max-width:760px)').matches;
          const next = Boolean(open && mobile);
          if (next) clearOtherExpandedView();
          card.classList.toggle('gdx-cob-v5-setup-x__i129-mobile-fullscreen', next);
          if (isTop) card.classList.toggle('gdx-cob-v5-setup-x__i128-mobile-fullscreen', next);
          master.classList.toggle('gdx-cob-v5-setup-x__i128-fullscreen-owner', next);
          detailModalNode?.classList.toggle('gdx-cob-v5__modal--i129-schematic-fullscreen', next);
          detailModalNode?.classList.toggle('gdx-cob-v5__modal--i128-schematic-fullscreen', next);
          shell.setAttribute('aria-expanded', next ? 'true' : 'false');
          shell.dataset.gdxCobV5I129Fullscreen = next ? '1' : '0';
          shell.dataset.gdxCobV5I129FullscreenReason = String(reason || 'tap');
          if (next) {
            if (sticker.parentElement !== card) card.appendChild(sticker);
            sticker.dataset.gdxCobV5I129Placement = 'overlay-root';
          } else {
            if (sticker.parentElement !== shell) shell.appendChild(sticker);
            sticker.dataset.gdxCobV5I129Placement = 'shell-overlay';
          }
          applyFullscreenStickerVisual(sticker, next);
          sticker.setAttribute('aria-label', labelTr + (next ? ' şemasını tam ekrandan kapat' : ' şemasını tam ekran aç'));
          const shareDock = ensureFullscreenShareDock(card);
          if (shareDock) {
            shareDock.hidden = !next;
            if (next) syncFullscreenShareDock(shareDock);
          }
          if (next) {
            requestAnimationFrame(() => {
              shell.scrollLeft = Math.max(0, (shell.scrollWidth - shell.clientWidth) / 2);
              shell.scrollTop = Math.max(0, (shell.scrollHeight - shell.clientHeight) / 2);
            });
          }
          return next;
        };
        const toggle = (reason = 'tap') => setExpanded(shell.getAttribute('aria-expanded') !== 'true', reason);
        shell.addEventListener('click', (event) => {
          if (event.target.closest('button,a,input,select,textarea')) return;
          toggle(kind + '-schematic-tap');
        });
        shell.addEventListener('keydown', (event) => {
          if (!['Enter',' '].includes(event.key)) return;
          event.preventDefault();
          toggle(kind + '-schematic-keyboard');
        });
        sticker.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          toggle(kind + '-sticker-tap');
        });
        return { setExpanded, toggle };
      };

      const topCard = el('section', 'gdx-cob-v5-setup-x__i104-view-card gdx-cob-v5-setup-x__i104-view-card--top');
      topCard.dataset.gdxCobV5FocusedView = '1';
      topCard.dataset.gdxCobV5Alt06CornerTab = '1';
      const topHead = el('div', 'gdx-cob-v5-setup-x__i125-top-head gdx-cob-v5-setup-x__i126-top-view-head');
      topHead.append(el('div', 'gdx-cob-v5-setup-x__i104-view-label', 'ÜSTTEN GÖRÜNÜM'), mirror);
      const topShell = el('div', 'gdx-cob-v5-setup-x__svg-shell gdx-cob-v5-setup-x__i104-top gdx-cob-v5-setup-x__i125-focused-shell gdx-cob-v5-setup-x__i126-fit-shell');

      /* FAZ08-I12.11.4A · LIVE TOP-VIEW BAND-SCALE PARITY.
         Same metric-domain logic as setup-share PNG. Node/card/font sizes remain fixed;
         only meter→pixel projection + radial meter rings change by setup distance domain. */
      const liveMetricPlan = typeof services.setupRendererStrict.bandScaledTopViewPlan === 'function'
        ? services.setupRendererStrict.bandScaledTopViewPlan(record)
        : null;
      const liveTopViewOptions = {
        side,
        theme:presentation.theme,
        width:1000,
        height:700,
        targetPresentation:presentation.targetPresentation
      };
      if (liveMetricPlan) Object.assign(liveTopViewOptions, {
        targetY:liveMetricPlan.targetY,
        maxScale:liveMetricPlan.maxScale,
        radialMeters:Array.from(liveMetricPlan.rings || []),
        metricScaleMode:liveMetricPlan.metricScaleMode
      });
      topCard.dataset.gdxCobV5I12114aLiveBandScale = liveMetricPlan?.key || 'LEGACY';
      topShell.dataset.gdxCobV5I12114aLiveBandScale = liveMetricPlan?.key || 'LEGACY';
      topShell.append(services.setupRendererStrict.topView(record, liveTopViewOptions));
      const topSticker = el('button', 'gdx-cob-v5-setup-x__i129-zoom-sticker');
      topShell.append(topSticker);
      topCard.append(topHead, topShell);
      wireI129ExpandableView(topCard, topShell, topSticker, 'top', 'Üstten Görünüm');

      const right = el('div', 'gdx-cob-v5-setup-x__i104-right');
      const sideCard = el('section', 'gdx-cob-v5-setup-x__i104-view-card gdx-cob-v5-setup-x__i104-view-card--side');
      sideCard.dataset.gdxCobV5Alt06CornerTab = '1';
      sideCard.append(el('div', 'gdx-cob-v5-setup-x__i104-view-label', 'YANDAN GÖRÜNÜM'));
      const sideShell = el('div', 'gdx-cob-v5-setup-x__i104-side');
      sideShell.append(services.setupRendererStrict.sideView(record, { side, theme:presentation.theme, width:760, height:560, targetPresentation:presentation.targetPresentation }));
      const sideSticker = el('button', 'gdx-cob-v5-setup-x__i129-zoom-sticker');
      sideShell.append(sideSticker);
      sideCard.append(sideShell);
      wireI129ExpandableView(sideCard, sideShell, sideSticker, 'side', 'Yandan Görünüm');

      const technical = el('section', 'gdx-cob-v5-setup-x__i104-technical');
      technical.append(el('h5', '', 'Teknik kurulum değerleri'));
      presentation.technicalRows.forEach((row) => {
        const item = el('div', 'gdx-cob-v5-setup-x__i104-tech-row');
        item.dataset.key = row.key;
        item.append(el('span', '', row.label), el('strong', '', row.value));
        technical.append(item);
      });
      right.append(sideCard, technical);
      visual.append(topCard, right);

      const nora = noraCard(context, record);
      nora.classList.add('gdx-cob-v5-setup-x__i104-nora');
      nora.dataset.gdxCobV5I104Nora = '1';

      master.append(head, metrics, visual, nora);
      mirror.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        side = side === 'LEFT' ? 'RIGHT' : 'LEFT';
        renderVariant(master.parentElement, context, record);
      });
      return master;
    }

    function renderVariant(host, context, record) {
      detailModalNode?.classList.remove('gdx-cob-v5__modal--i128-schematic-fullscreen','gdx-cob-v5__modal--i129-schematic-fullscreen');
      host.replaceChildren();
      host.dataset.activeVariant = record.variantKey;
      host.dataset.gdxCobV5I104FinalGeometryHybrid = '1';
      host.append(
        i104MasterCard(context, record),
        r74AlternativeComment(context, record)
      );
    }

    function render(offerKey, options = {}) {
      const mount = document.getElementById(services.config.ids.detailSetup);
      if (!mount) return false;
      if (options.captureFallback === true || !mount.querySelector('.gdx-cob-v5-setup-x')) snapshotFallback(mount);

      const context = contextFor(offerKey);
      if (!context.ok) {
        blockedCount += 1;
        lastHoldReason = context.reason;
        if (mount.querySelector('.gdx-cob-v5-setup-x')) restoreFallback(mount);
        diagnostics.setupExperienceStrict = {
          ...diagnostics.setupExperienceStrict,
          lastOfferKey: offerKey || null,
          lastRenderStatus: 'FALLBACK_PRESERVED_NO_UNIVERSAL_BINDING',
          lastHoldReason,
          blockedCount
        };
        return false;
      }

      const previousRenderState = Object.freeze({
        offerKey: currentOfferKey,
        purposeKey: currentContext?.purposeKey || null,
        lane: currentContext?.lane || null,
        activeVariant,
        side
      });
      const sameDecisionContext =
        previousRenderState.offerKey === String(offerKey) &&
        previousRenderState.purposeKey === context.purposeKey &&
        previousRenderState.lane === context.lane;
      const preservedVariant = sameDecisionContext &&
        context.records.some((record) => record.variantKey === previousRenderState.activeVariant)
        ? previousRenderState.activeVariant
        : 'MAIN';

      currentOfferKey = String(offerKey);
      currentContext = context;
      activeVariant = preservedVariant;
      side = sameDecisionContext ? previousRenderState.side : 'LEFT';
      lastHoldReason = null;

      mount.replaceChildren();
      const root = el('section', 'gdx-cob-v5-setup-x gdx-cob-v5-setup-x--r74');
      root.dataset.offerKey = currentOfferKey;
      root.dataset.bindingSource = context.bindingSource;
      root.dataset.bindingTier = context.bindingTier;
      root.dataset.bindingOwner = context.bindingOwner;
      root.dataset.r74Architecture = 'P1_P2_P3_P4_SINGLE_MODAL';

      const head = el('header', 'gdx-cob-v5-setup-x__head');
      const headText = el('div', 'gdx-cob-v5-setup-x__head-copy');
      const main = context.records.find((record) => record.variantKey === 'MAIN') || context.records[0];
      headText.append(
        el('p', 'gdx-cob-v5-setup-x__eyebrow', setupSurfaceText(main.mainGroupLabel || 'Ölçülü Dinamik Setup')),
        el('h3', '', context.result.purposeLabel),
        el('p', 'gdx-cob-v5-setup-x__product-title', context.recommendationItem.title || context.detail.title),
        el('p', 'gdx-cob-v5-setup-x__summary', 'Ana ve alternatif kurulum bilgileri aynı pencerede, tek bir akışta sunulur.')
      );
      const lane = el('span', 'gdx-cob-v5-setup-x__lane', context.result.laneLabelTr || (context.lane === 'SINGLE_READY_SET' ? 'Tek Işıklı Hazır Set' : 'Yalnız Ana Işık Ünitesi'));
      head.append(headText, lane);
      root.append(head);

      const nav = el('nav', 'gdx-cob-v5-setup-x__r74-nav');
      nav.setAttribute('aria-label', 'Kurulum bilgi akışı');
      const navButtons = [];
      const setActiveJump = (activeCode, reason = 'nav-click') => {
        const normalized = ['P1','P2','P3','P4'].includes(String(activeCode || '').toUpperCase())
          ? String(activeCode).toUpperCase() : 'P1';
        navButtons.forEach((btn) => {
          const active = btn.dataset.r74Jump === normalized;
          btn.setAttribute('aria-current', active ? 'true' : 'false');
          btn.dataset.gdxCobV5I128ScrollSpyActive = active ? '1' : '0';
        });
        i128ActivePanelCode = normalized;
        root.dataset.gdxCobV5I128ActivePanel = normalized;
        root.dataset.gdxCobV5I128ActiveReason = String(reason || 'nav-click');
      };
      [
        ['P1', 'Ürün ve Amaç'],
        ['P2', 'Profil ve Kurulum'],
        ['P3', 'Şema ve NORA'],
        ['P4', 'Alternatif ve Dikkat']
      ].forEach(([code, label], index) => {
        const button = el('button', 'gdx-cob-v5-setup-x__r74-nav-button', label);
        button.type = 'button';
        button.dataset.r74Jump = code;
        button.setAttribute('aria-current', index === 0 ? 'true' : 'false');
        button.addEventListener('click', () => {
          setActiveJump(code, 'nav-click');
          const target = root.querySelector(`[data-r74-panel="${code}"]`);
          if (target && String(target.tagName).toLowerCase() === 'details') target.open = true;
          target?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        });
        navButtons.push(button);
        nav.append(button);
      });
      root.append(nav);

      const p1 = r74P1(context);
      root.append(p1);

      const p2 = r74Section(
        'P2',
        'Kullanım profili ve dinamik kurulum',
        'Kullanım profili ile ana ve alternatif kurulum seçenekleri birlikte gösterilir.'
      );
      p2.append(r74UsageProfile(context));
      const tabs = el('div', 'gdx-cob-v5-setup-x__variants');
      tabs.setAttribute('role', 'tablist');
      tabs.setAttribute('aria-label', 'Kurulum seçenekleri');
      /* FAZ08-I12.2: duplicate selected-setup summary removed.
         P2 keeps only usage profile + variant chooser; canonical selected setup details remain in P3. */
      p2.append(tabs);
      root.append(p2);

      const p3 = r74Section(
        'P3',
        'NORA özeti, kurulum şeması ve ölçüler',
        'Seçili kurulum; üstten görünüm şeması, ölçüler, Kelvin başlangıcı ve NORA yorumu ile birlikte gösterilir.'
      );
      const body = el('div', 'gdx-cob-v5-setup-x__body');
      body.id = 'gdxCobV5DynamicSetupBody';
      body.setAttribute('role', 'tabpanel');
      p3.append(body);
      root.append(p3);

      const p4 = el('details', 'gdx-cob-v5-setup-x__r74-panel gdx-cob-v5-setup-x__r74-panel--p4 gdx-cob-v5-setup-x__r74-disclosure');
      /* COB-FAZ07-I8.6 · ALTERNATIVE / CAUTION DEFAULT-CLOSED */
      p4.open = false;
      p4.dataset.r74Panel = 'P4';
      p4.dataset.gdxCobV5Faz04AlternativeDisclosure = '1';
      p4.dataset.gdxCobV5Faz04DefaultOpen = '0';
      p4.dataset.gdxCobV5I86AlternativeDefaultClosed = '1';
      p4.id = 'gdxCobV5R74P4';
      const p4Summary = el('summary', 'gdx-cob-v5-setup-x__r74-disclosure-summary');
      const p4SummaryLabel = el('strong', '');
      const syncP4Summary = () => {
        p4SummaryLabel.textContent = p4.open ? 'Kapat' : 'Alternatif Kurulumu ve Dikkat Noktalarını Göster';
      };
      p4Summary.append(
        el('span', 'gdx-cob-v5-setup-x__r74-phase-code', 'ALTERNATİF'),
        p4SummaryLabel
      );
      syncP4Summary();
      p4.addEventListener('toggle', syncP4Summary);
      p4.append(p4Summary);
      const alternativesHost = el('div', 'gdx-cob-v5-setup-x__r74-alternatives-host');
      const riskHost = el('div', 'gdx-cob-v5-setup-x__r74-risk-host');
      p4.append(alternativesHost, riskHost);
      root.append(p4);

      function choose(variantKey, chooseOptions = {}) {
        const record = context.records.find((row) => row.variantKey === variantKey) || context.records[0];
        activeVariant = record.variantKey;
        tabs.querySelectorAll('[role="tab"]').forEach((tab) => {
          const selected = tab.dataset.variant === activeVariant;
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.tabIndex = selected ? 0 : -1;
        });
        /* FAZ08-I12.2: no duplicate selection summary render here. */
        renderVariant(body, context, record);
        alternativesHost.replaceChildren(r74AlternativeEvaluation(context, record));
        riskHost.replaceChildren(r74RiskBlock(context, record));
        if (chooseOptions.focusSchematic === true) {
          requestAnimationFrame(() => focusSetupSchematic('variant-schematic-focus'));
        }
      }

      context.records.forEach((record) => {
        const tab = el('button', 'gdx-cob-v5-setup-x__variant');
        tab.type = 'button';
        tab.dataset.variant = record.variantKey;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', body.id);
        tab.setAttribute('aria-selected', record.variantKey === activeVariant ? 'true' : 'false');
        tab.tabIndex = record.variantKey === activeVariant ? 0 : -1;
        tab.append(
          el('span', 'gdx-cob-v5-setup-x__variant-tag', variantSetupLabel(record)),
          el('strong', '', record.variantKey === 'MAIN' ? 'Öncelikli kurulum' : setupSurfaceText(record.variantReasonTr || variantSetupLabel(record))),
          el('small', '', `${services.setupRendererStrict.fmtM(record.lightToTargetM)} · ${services.setupRendererStrict.fmtDeg(record.horizontalAngleDeg)} · ${record.lightBand || '—'}`)
        );
        if (record.variantKey !== 'MAIN' && record.variantReasonTr) {
          tab.append(el('span', 'gdx-cob-v5-setup-x__variant-reason', setupSurfaceText(record.variantReasonTr)));
        } else if (record.variantKey === 'MAIN') {
          tab.append(el('span', 'gdx-cob-v5-setup-x__variant-reason', 'Bu ürün ve amaç için öncelikli kurulum geometrisi.'));
        }
        tab.addEventListener('click', () => choose(record.variantKey, { focusSchematic: true }));
        tabs.append(tab);
      });

      tabs.addEventListener('keydown', (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const all = [...tabs.querySelectorAll('[role="tab"]')];
        let index = all.indexOf(tab);
        if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = all.length - 1;
        else index = (index + (event.key === 'ArrowRight' ? 1 : -1) + all.length) % all.length;
        all[index].focus();
        choose(all[index].dataset.variant, { focusSchematic: true });
      });

      mount.append(root);
      installI128ScrollSpy(root, navButtons);
      choose(activeVariant);
      syncModalMode();
      requestAnimationFrame(() => syncI128ScrollSpy('render-complete'));

      renderedCount += 1;
      if (context.bindingTier !== 'EXACT') standardRenderCount += 1;
      diagnostics.setupExperienceStrict = {
        ...diagnostics.setupExperienceStrict,
        phase: 'DYN324-R7.4.1',
        lastOfferKey: currentOfferKey,
        lastPurposeKey: context.purposeKey,
        lastPurchaseLane: context.lane,
        lastHeadFamilyKey: context.headFamilyKey,
        lastRenderStatus: 'R7_4_P1_P2_P3_P4_SINGLE_MODAL_RENDERED',
        lastBindingSource: context.bindingSource,
        lastBindingReason: context.bindingReason,
        lastBindingTier: context.bindingTier,
        lastBindingOwner: context.bindingOwner,
        lastStandardReferencePurposeKey: context.standardReferencePurposeKey,
        lastStandardReferenceHeadFamilyKey: context.standardReferenceHeadFamilyKey,
        lastStandardReferenceOfferKey: context.standardReferenceOfferKey,
        lastHoldReason: null,
        informationArchitecture: 'P1_P2_P3_P4_SINGLE_DETAIL_MODAL',
        r74SectionCount: 4,
        secondWideSchematic: false,
        scoreOwnerMutation: false,
        rankingMutation: false,
        renderedCount,
        standardRenderCount,
        blockedCount
      };
      return true;
    }

    function init() {
      if (initialized) return;
      ensureDetailNodes();
      syncI126PrimaryTabs();
      installI127AutoCompact();
      ensureGlobalNodes();
      syncGlobalToTop();
      unlistenDetail = events.on('detail:opened', (event) => {
        requestAnimationFrame(() => {
          syncI126PrimaryTabs();
          const mount = document.getElementById(services.config.ids.detailSetup);
          snapshotFallback(mount);
          render(event.detail?.offerKey, { captureFallback: false });
          resetDetailScroll('detail-opened');
          syncModalMode();
          syncI127AutoCompact('detail-opened');
          syncI128ScrollSpy('detail-opened');
        });
      });
      unlistenRecommendation = events.on('recommendation:ready', () => {
        if (currentOfferKey && detailModalNode && !detailModalNode.hidden) {
          requestAnimationFrame(() => render(currentOfferKey, { captureFallback: false }));
        }
      });

      tabHandler = (event) => {
        const tab = event.target.closest('[data-tab]');
        if (!tab) return;
        syncModalMode(tab.dataset.tab);
        requestAnimationFrame(() => {
          syncModalMode();
          resetDetailScroll('detail-tab-change');
          syncI127AutoCompact('detail-tab-change');
          syncI128ScrollSpy('detail-tab-change');
        });
      };
      detailTabsNode?.addEventListener('click', tabHandler, true);
      detailTabsNode?.addEventListener('keydown', tabHandler, true);

      if (detailTabsNode && typeof MutationObserver === 'function') {
        tabObserver = new MutationObserver(() => {
          syncI126PrimaryTabs();
          syncModalMode();
          syncI127AutoCompact('tab-observer');
        });
        tabObserver.observe(detailTabsNode, { subtree: true, attributes: true, attributeFilter: ['aria-selected'] });
      }
      if (detailModalNode && typeof MutationObserver === 'function') {
        modalObserver = new MutationObserver(() => {
          if (detailModalNode.hidden || detailModalNode.getAttribute('aria-hidden') === 'true') {
            detailModalNode.classList.remove('gdx-cob-v5__modal--setup-wide');
            setI127CompactState(false, 'modal-hidden');
            detailModalNode.classList.remove('gdx-cob-v5__modal--i128-schematic-fullscreen','gdx-cob-v5__modal--i129-schematic-fullscreen');
            currentOfferKey = null;
            currentContext = null;
          } else {
            syncModalMode();
            syncI127AutoCompact('modal-visible');
          }
          syncGlobalToTop();
        });
        modalObserver.observe(detailModalNode, { attributes: true, attributeFilter: ['hidden', 'aria-hidden'] });
      }
      if (shellNode && typeof MutationObserver === 'function') {
        shellObserver = new MutationObserver(syncGlobalToTop);
        shellObserver.observe(shellNode, { attributes: true, attributeFilter: ['hidden', 'aria-hidden'] });
      }
      if (rootNode && typeof MutationObserver === 'function') {
        themeObserver = new MutationObserver(() => {
          if (currentOfferKey && currentContext && modalVisible()) {
            requestAnimationFrame(() => render(currentOfferKey, { captureFallback: false }));
          }
        });
        themeObserver.observe(rootNode, { attributes: true, attributeFilter: ['data-gdx-cob-v5-theme'] });
      }

      initialized = true;
      diagnostics.setupExperienceStrict = {
        phase: 'DYN324-R7.4.1',
        initialized: true,
        bindingStrategy: 'U5_UNIVERSAL_EXACT_FIRST_THEN_STANDARD_PURPOSE_ARCHETYPE',
        rankFallbackAllowed: false,
        exactFirst: true,
        standardFallbackAllowed: true,
        universalResolverOwner: 'setupStandardLibrary',
        customerStandardBindingActive: true,
        topViewOnly: false,
        sideViewRendered: true,
        i104FinalGeometryHybrid: true,
        i104ExactShareParityRequired: true,
        i104ThemeRerenderObserver: true,
        sceneHiddenUntilApprovedHttpsCdn: true,
        packageTruthOwner: 'ACTUAL_OFFER_RUNTIME',
        fixturePackageTruthRendered: false,
        fixtureProductTitleRendered: false,
        detailTabScrollReset: true,
        variantTabScrollReset: false,
        variantSchematicFocus: true,
        setupWideOnlyOnSetupTab: true,
        i126CompactDualStickyPrimaryTabs: true,
        i126StandaloneGalleryTabSuppressed: true,
        i126TopViewMirrorHeaderChip: true,
        i127AutoCompactingSticky: true,
        i127CompactScrollSurface: 'DETAIL_MODAL_BODY',
        i127CompactEnterAtPx: 112,
        i127CompactLeaveAtPx: 28,
        i127SchematicSizingMutation: false,
        i127MirrorTopViewHeaderRight: true,
        i128ScrollSpyActiveNav: true,
        i128ScrollSpySurface: 'DETAIL_MODAL_BODY',
        i128MobileSchematicTapFullscreen: true,
        i128FullscreenGeometryClone: false,
        i128FullscreenUsesSameSvgDom: true,
        i129DualSchematicFullscreen: true,
        i129TopAndSideSameBehavior: true,
        i129MobileZoomSticker: 'CIRCLE_PLUS_MINUS_THEME_AWARE',
        schematicMode: 'INLINE_ONLY',
        schematicWideToggleRemoved: true,
        mandatoryProductHero: true,
        informationArchitecture: 'P1_P2_P3_P4_SINGLE_DETAIL_MODAL',
        r74SectionCount: 4,
        secondWideSchematic: false,
        scoreOwnerMutation: false,
        rankingMutation: false,
        variantSelectionPreservedOnSameContextRerender: true,
        mobileInlineViewport: Object.freeze({ width: 680, height: 560 }),
        nestedModalCreated: false,
        faz09102ExternalFullscreenBridge: true,
        externalFullscreenDesktopSupported: true,
        externalFullscreenMobileSupported: true,
        externalFullscreenSameSvgDom: true,
        globalToTopOwner: 'COB_MOTOR_ROOT',
        globalToTopPanelSurface: true,
        globalToTopDetailSurface: true,
        renderedCount,
        blockedCount
      };
    }

    function destroy() {
      unlistenDetail?.();
      unlistenRecommendation?.();
      unlistenDetail = null;
      unlistenRecommendation = null;
      detailTabsNode?.removeEventListener('click', tabHandler, true);
      detailTabsNode?.removeEventListener('keydown', tabHandler, true);
      tabObserver?.disconnect();
      modalObserver?.disconnect();
      shellObserver?.disconnect();
      themeObserver?.disconnect();
      tabObserver = null;
      modalObserver = null;
      shellObserver = null;
      themeObserver = null;
      uninstallI127AutoCompact();
      uninstallI128ScrollSpy();
      globalToTopNode?.removeEventListener('click', resetActiveSurfaceScroll);
      globalToTopNode?.remove();
      detailModalNode?.classList.remove('gdx-cob-v5__modal--setup-wide','gdx-cob-v5__modal--i127-auto-compact','gdx-cob-v5__modal--i128-schematic-fullscreen','gdx-cob-v5__modal--i129-schematic-fullscreen');
      detailTabsNode = null;
      detailBodyNode = null;
      detailModalNode = null;
      rootNode = null;
      shellNode = null;
      workspaceNode = null;
      globalToTopNode = null;
      tabHandler = null;
      currentOfferKey = null;
      currentContext = null;
      fallbackSnapshot = null;
      initialized = false;
    }

    function experienceRecordById(setupRecordId) {
      const key = String(setupRecordId || '');
      const current = currentContext?.records?.find((record) =>
        String(record?.setupRecordId || record?.recordId || '') === key
      ) || null;
      if (current) return current;

      return (
        services.setupGeometryStrict.getResolvedById(setupRecordId) ||
        services.setupGeometryStrict.getById(setupRecordId) ||
        null
      );
    }

    return Object.freeze({
      init,
      destroy,
      refresh() {
        if (currentOfferKey) render(currentOfferKey, { captureFallback: false });
      },
      render,
      resolveContextForQa(offerKey, purposeKey) {
        return resolveContextForPurpose(offerKey, purposeKey, null);
      },
      evaluateCompatibility(offerKey, setupRecordId) {
        const offer = services.catalog.getOffer(offerKey);
        const record = experienceRecordById(setupRecordId);
        return offer && record ? evaluateCompatibility(offer, record) : null;
      },
      evaluateStandCompatibility(offerKey, setupRecordId) {
        const offer = services.catalog.getOffer(offerKey);
        const record = experienceRecordById(setupRecordId);
        return offer && record ? evaluateStandCompatibility(offer, record) : null;
      },
      projectVariantNarrative(offerKey, setupRecordId) {
        if (!currentContext || String(currentContext.offerKey) !== String(offerKey)) return null;
        const record = experienceRecordById(setupRecordId);
        return record ? projectVariantNarrative(currentContext, record) : null;
      },
      wireExternalExpandableView,
      getExternalFullscreenState: () => externalFullscreenHandle?.getState?.() || Object.freeze({ expanded:false }),
      getState: () => Object.freeze({
        initialized,
        currentOfferKey,
        currentPurposeKey: currentContext?.purposeKey || null,
        currentPurchaseLane: currentContext?.lane || null,
        currentBindingSource: currentContext?.bindingSource || null,
        currentBindingTier: currentContext?.bindingTier || null,
        currentBindingOwner: currentContext?.bindingOwner || null,
        standardRenderCount,
        activeVariant,
        side,
        schematicMode: 'INLINE_ONLY',
        mobileInline: inlineSchematicOptions().mobile,
        renderedCount,
        blockedCount,
        compatibilityRenderCount,
        lastScrollResetReason,
        lastHoldReason,
        globalToTopExists: Boolean(globalToTopNode?.isConnected),
        globalToTopHidden: Boolean(globalToTopNode?.hidden),
        globalToTopSurface: globalToTopNode?.dataset.surface || null,
        detailTabScrollReset: Boolean(tabHandler),
        informationArchitecture: 'P1_P2_P3_P4_SINGLE_DETAIL_MODAL',
        r74SectionCount: currentOfferKey ? 4 : 0,
        secondWideSchematic: false,
        scoreOwnerMutation: false,
        rankingMutation: false,
        variantSelectionPreservedOnSameContextRerender: true,
        i88VariantNarrativeProjector: true,
        i88PackageStandTruth: true,
        i104FinalGeometryHybrid: true,
        i104ExactShareParityRequired: true,
        i104VariantAutoScrollTarget: 'I104_MASTER',
        i104VariantAutoScrollRestored: true
      })
    });
  });
})();
